import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  Activity,
  ArrowLeft,
  Ban,
  Cable,
  ChevronDown,
  CircleCheck,
  Cloud,
  CloudOff,
  Eraser,
  Loader2,
  MapPin,
  Pencil,
  Power,
  PowerOff,
  Radio,
  RefreshCw,
  Replace,
  Router,
  Server,
  RotateCcw,
  RotateCw,
  ShieldCheck,
  Settings2,
  SlidersHorizontal,
  StickyNote,
  Trash2,
  Wifi,
} from 'lucide-react';
import {
  onuHealthLabel,
  onuHealthTone,
  signalBand,
  TR069_INFORM_GRACE_MS,
  TR069_SILENCE_CAUSES,
  TR069_STALE_CAUSES,
  wifiOwnedByRouter,
  type CliActionResult,
  type JobHandle,
  type DeviceDetail,
  type Olt,
  type Onu,
  type SecurityState,
} from '@simpleacs/shared';
import { api } from '../lib/api';
import { runJob } from '../lib/jobs';
import { onuPath } from '../lib/links';
import { useTasks } from '../lib/tasks';
import { formatRelative, formatTimestamp, formatDate, formatDateTime, formatDayMonth } from '../lib/format';
import { useSession } from '../lib/session';
import {
  DisableDialog,
  DisabledBanner,
  DisabledPill,
} from '../components/OutOfService';
import { VlansTab } from './device/VlansTab';
import { PortsTab } from './device/PortsTab';
import { MacTableTab } from './device/MacTableTab';
import { OnuActionBar, type OnuAction } from './device/OnuActionBar';
import { WanModeDialog } from './device/WanModeDialog';
import { RenameDialog } from './device/RenameDialog';
import { NoteDialog } from './device/NoteDialog';
import { OmciConfigCards } from './device/OmciConfigCards';
import { OltRunningConfigView } from './device/OltConfigTab';
import { OpticsCard } from './device/OpticsCard';
import { TrafficCard } from './device/TrafficCard';
import { LineTestCard } from './device/LineTestCard';
import { SettingsCard } from './device/SettingsCard';
import { PortForwards } from './device/SecurityCard';
import { SecurityGroupControls, OmciFirewall } from './device/SecurityControls';
import { ConfigSectionCard, DiagnosticsCard } from './device/ConfigSections';
import { EventsTab } from './device/EventsTab';
import { SwapsTab } from './device/SwapsTab';
import { IptvTab, VoipTab } from './device/ServiceTabs';
import { AllSettings } from './device/AllSettings';
import { otherWanConnections, WanConnectionCard, WanDeleteButton, wanConnectionTitle, wanDeletable } from './device/WanConnections';
import { WanPorts } from './device/WanPorts';
import { SwapOnuDialog } from './device/SwapOnuDialog';
import { ManagementModeDialog } from './device/ManagementModeDialog';
import type { QueryKey } from '@tanstack/react-query';
import type { DeviceRefreshScope } from '@simpleacs/shared';
import { RefreshButton } from '../components/RefreshButton';
import { ResyncConfirmDialog, type ResyncApplyOptions } from './device/ResyncConfirmDialog';
import { ApplyDevicePresetDialog } from './device/ApplyDevicePresetDialog';
import {
  AcsDeviceFacts,
  DeviceRecordEditor,
  HostsCard,
  type LearnedMac,
  LanCard,
  lanCardTitle,
  OnuOltActionDialog,
  TasksTab,
  useSummonUnreadOnce,
  WanCard,
  wanCardTitle,
  ManagementWanCard,
  WifiCard,
  wifiCardTitle,
} from './DeviceDetail';
import {
  CollapsibleGroup,
  DraftScope,
  useCollapsibleItem,
  useDraftHolder,
} from '../components/ui/CollapsibleGroup';
import { configsInitialOpenKey, panelBodyMounted } from '../lib/accordion';
import { wanIpv6QueryScope } from '../lib/wan-ipv6';
import { firewallTitlePill } from '../lib/wan-apply';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  Pill,
  SearchSelect,
  Spinner,
  StatCard,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';

interface OnuDetailResponse {
  onu: Onu;
  olt: Pick<Olt, 'id' | 'number' | 'name' | 'vendor' | 'model'>;
  /** The light path when the ONU sits on an anchored splitter chain. */
  path: { stages: Array<{ name: string; kind: string }>; feedPonPort: string | null } | null;
}

interface OpticalDay {
  day: number;
  rxMin: number | null;
  rxMax: number | null;
  rxLast: number | null;
  oltRxLast: number | null;
  samples: number;
}

interface OpticalHistory {
  items: OpticalDay[];
  verdict: {
    status: 'ok' | 'degrading' | 'critical' | 'insufficient-data';
    recentDbm?: number;
    baselineDbm?: number;
    detail?: string;
  };
}


/** The light chart's window. The caption quotes it, so they move together. */
const LIGHT_WINDOW_DAYS = 7;

type Tab =
  | 'overview'
  | 'configs'
  | 'security'
  | 'olt-config'
  | 'vlans'
  | 'ports'
  | 'macs'
  | 'voip'
  | 'iptv'
  | 'events'
  | 'swaps'
  | 'tasks';

/** Which config editor the overview linked to, so Configs opens on the right one. */
type ConfigFocus = 'wan' | 'lan' | 'wifi' | 'security';

/** The PON section's accent, so the Configs tab matches the ONUs nav item. */
const ONU_ACCENT = '#10b981';

const TABS: Array<[Tab, string]> = [
  ['overview', 'Overview'],
  // The two config surfaces lead, right after Overview: what this platform WRITES
  // to the ONU comes before the chassis-side inventory an operator scans less
  // often. Every TR-069 and OMCI config lives under Configs, mode-aware — on
  // TR-069 the CPE's own WAN/LAN/WiFi editors and port-forwards, on OMCI what the
  // OLT can carry.
  ['configs', 'Configs'],
  // The CPE's security surface — firewall level, per-service WAN/LAN access,
  // accounts — rendered from what the model actually exposes.
  ['security', 'Security'],
  // The chassis's own words for this ONU — the raw running config, the truth the
  // parsed Configs/Security views are an interpretation of. Sits beside them so
  // "what is configured" and "what the OLT actually holds" are one glance apart.
  ['olt-config', 'OLT config'],
  ['vlans', 'VLANs'],
  ['ports', 'Ports'],
  // Who is behind the ONU, by MAC: what the OLT learned on its VLANs joined
  // with the hosts the router reports (operator, 2026-10-16).
  ['macs', 'MAC table'],
  // The two services the OLT switches per ONU (operator, 2026-09-26), beside
  // the VLANs and ports they ride on. VoIP also carries the router's own SIP
  // line status, which used to be a panel under Configs.
  ['voip', 'VoIP'],
  ['iptv', 'IPTV'],
  ['events', 'Events'],
  // Beside Events and not inside it, deliberately. An event is something that
  // happened TO this ONU; a swap is the point at which "this ONU" stopped
  // meaning the same hardware, and reading the two as one list invites blaming
  // a subscriber's outage on the box that replaced it.
  ['swaps', 'Swaps'],
  ['tasks', 'Tasks & faults'],
];

/**
 * One ONU, however it is managed.
 *
 * Every ONU on a PON gets the same page: the chassis always knows where it is,
 * what light it has and why it last changed state, and that half is drawn from
 * the OLT-side inventory whether or not the ONU has ever spoken to an ACS. When
 * the ONU is on SimpleACS's TR-069 the page fills in the rest — its WAN, LAN,
 * wifi, VLANs and tasks. When it is not, those tabs say so plainly rather than
 * vanishing, so an operator never has to work out which of two pages they are
 * looking at.
 *
 * The URL takes either the inventory id or the linked device id, so links built
 * from the ACS side and links built from the chassis land in the same place.
 */
export function OnuDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const [tab, setTab] = useState<Tab>('overview');
  /** Which config editor the overview asked to jump to, cleared once consumed. */
  const [configFocus, setConfigFocus] = useState<ConfigFocus | null>(null);
  const goToConfig = (focus: ConfigFocus) => {
    // Security is its own tab now; the rest open the Configs tab on that editor.
    if (focus === 'security') {
      setTab('security');
      return;
    }
    setConfigFocus(focus);
    setTab('configs');
  };
  const [notice, setNotice] = useState<string | null>(null);
  const [noticeTone, setNoticeTone] = useState<'info' | 'danger'>('info');
  // One sink for every banner on the page, so a refusal reads red and a later
  // success clears it back to info — a 403 must never wear the success colour.
  const notify = (message: string, tone: 'info' | 'danger' = 'info') => {
    setNotice(message);
    setNoticeTone(tone);
  };
  const [modeOpen, setModeOpen] = useState(false);
  const [wanOpen, setWanOpen] = useState(false);
  const [presetOpen, setPresetOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [noting, setNoting] = useState(false);
  /** The reason prompt, open only on the way OUT of service. */
  const [disabling, setDisabling] = useState(false);
  const [rebootPlan, setRebootPlan] = useState<CliActionResult | null>(null);
  const [oltAction, setOltAction] = useState<null | 'recreate' | 'deauthorize' | 'factory-reset'>(null);
  /** The Swap wizard: a different unit takes this subscriber's slot. */
  const [swapOpen, setSwapOpen] = useState(false);
  /**
   * The live-read health, so a chassis that stops answering during the auto
   * refresh says so and keeps the last-known values, rather than the failure
   * flashing past as one toast. `lastGoodAt` stamps the newest good read so the
   * banner can say how stale the screen is.
   */
  const [readError, setReadError] = useState<string | null>(null);
  const [lastGoodAt, setLastGoodAt] = useState<number | null>(null);

  const query = useQuery({
    queryKey: ['onu', id],
    queryFn: () => api.get<OnuDetailResponse>(`/onus/${encodeURIComponent(id)}`),
    // The operator is looking right at it; keep it live.
    refetchInterval: 5_000,
    // Keep the last ONU painted while the next one loads, so moving between ONUs
    // never blanks to a spinner — the page is a DB read, not a chassis wait.
    placeholderData: keepPreviousData,
  });

  const onu = query.data?.onu;
  const olt = query.data?.olt;
  const lightPath = query.data?.path ?? null;
  const deviceId = onu?.deviceId ?? null;
  /**
   * How this ONU is managed IN FACT, from the chassis rather than from what we
   * once asked for.
   *
   * Falls back to the old question — is there a device record — only while no
   * chassis read has happened, so an unread ONU is described by the evidence
   * that does exist rather than declared OMCI on none.
   */
  /**
   * Between "it has not answered yet" and "it is not going to".
   *
   * Both messages below are about the same ONU and used to be able to appear
   * about the same one: the waiting banner fires on the INTENT column with no
   * time bound, so it said "nothing more to do here" for ever, and the
   * diagnostic panel says the opposite. One boundary decides which is showing,
   * and the banner keeps its own condition so an ONU whose chassis has not been
   * re-read since the write still gets the patient message rather than none.
   */
  const informGraceOpen =
    onu?.managementSetAt != null && Date.now() - onu.managementSetAt <= TR069_INFORM_GRACE_MS;

  const managementState: 'omci' | 'tr069' | 'tr069_other' =
    onu?.managementState ?? (deviceId ? 'tr069' : 'omci');
  /**
   * What the mode PILL shows, which is not always what the chassis is on.
   *
   * A bridged subscriber informing our ACS is on our TR-069 in fact while the
   * OLT's own config carries no ACS line, so it reads OMCI from the chassis. The
   * pill says TR-069 (with the ⚠ and the banner below) so the operator sees the
   * truth — but `managementState` above stays the CHASSIS answer, because that
   * is what the management-mode / VLAN dialogs must send back: upgrading it there
   * would turn a VLAN-only edit into a write that puts the ONU on OLT-delivered
   * TR-069, which is the exact config this only means to FLAG.
   */
  const displayMode: 'omci' | 'tr069' | 'tr069_other' = onu?.oltAcsMismatch
    ? 'tr069'
    : managementState;
  // The TR-069 tab is not offered for an OMCI ONU, so a page that lands on it
  // — from a stale link, or an ONU switched to OMCI while open — falls back
  // rather than rendering a tab that is no longer in the strip.
  const activeTab: Tab = tab;
  /**
   * Where this ONU's VLAN and port configuration lives. The device owns it when
   * one exists, so a subscriber who later informs an ACS keeps what was already
   * configured against them.
   */
  const configBase = deviceId
    ? `/devices/${encodeURIComponent(deviceId)}`
    : `/onus/${encodeURIComponent(id ?? '')}`;


  // The TR-069 half, fetched only when there is one. Its cache key matches the
  // one the embedded cards use, so they all read the same record.
  const deviceQuery = useQuery({
    queryKey: ['device', deviceId],
    queryFn: () => api.get<DeviceDetail>(`/devices/${encodeURIComponent(deviceId!)}`),
    enabled: Boolean(deviceId),
    refetchInterval: 30_000,
  });
  const device = deviceQuery.data ?? null;

  /**
   * The MACs the OLT has learned behind this ONU.
   *
   * The same snapshot the ONU card reads, so it costs no extra chassis time:
   * the read that fetches the ONU's configuration fetches this in the same CLI
   * session, and both sides share the cache.
   */
  const learnedMacs = useQuery({
    queryKey: ['onu', onu?.id ?? id, 'omci-config'],
    queryFn: () =>
      api.get<{ macs?: LearnedMac[]; fetchedAt?: number | null; cached?: boolean; unread?: boolean }>(
        `/olts/${encodeURIComponent(onu!.oltId)}/onus/${encodeURIComponent(onu!.id)}/omci-config`,
      ),
    enabled: Boolean(onu?.oltId),
  });

  /**
   * The chassis read is what teaches this platform which ACS the ONU answers
   * to, and it happens in a DIFFERENT request from the one that renders the
   * pill.
   *
   * `adoptFromRead` records the ACS from the OMCI read; the ONU row itself is
   * fetched separately and had already answered by then, with `acsReadAt` still
   * null and the pill falling back to "has it ever informed us". On an ONU
   * pointed at somebody else's ACS that fallback says TR-069 — the exact thing
   * the third state exists to stop it saying. So when the read lands, the row
   * is asked again.
   */
  useEffect(() => {
    if (!learnedMacs.isSuccess) return;
    /*
      `exact`, and keyed the way the ROW's query is keyed — on the URL id, which
      may be the ONU's number rather than its id.

      Without `exact` this also matches `['onu', id, 'omci-config']`, and
      invalidating the read that just finished re-runs it, which lands here
      again: an unbroken loop of CLI sessions against the chassis, started by an
      effect meant to refresh a pill.
    */
    void queryClient.invalidateQueries({ queryKey: ['onu', id], exact: true });
  }, [learnedMacs.isSuccess, learnedMacs.dataUpdatedAt, id, queryClient]);

  // Stored daily readings — no OLT contact, so it costs nothing to show.
  const opticsQuery = useQuery({
    queryKey: ['onu', onu?.id ?? id, 'optics'],
    queryFn: () =>
      api.get<OpticalHistory>(
        // A week: the question this chart answers is "has anything changed
        // lately", and ninety days squeezed recent movement into noise. The
        // nightly degradation check still judges against its own longer window.
        `/olts/${encodeURIComponent(onu!.oltId)}/onus/${encodeURIComponent(onu!.id)}/optics?days=${LIGHT_WINDOW_DAYS}`,
      ),
    enabled: Boolean(onu?.oltId),
  });

  /**
   * Reading one ONU's optics costs a CLI session, so it is a button rather than
   * something the page does on its own. ZTE only: Huawei reports optics over
   * SNMP on every sync, so there is nothing on-demand to fetch.
   */
  /**
   * Everything this page shows, re-read from where it actually lives.
   *
   * Three sources, because the ONU is three things at once. The chassis knows
   * its light levels and whether it is up; the chassis also knows the service
   * it carries — WAN and management addressing, the VLANs, the hosts behind it.
   * If the subscriber is on TR-069, the ACS knows what the ONU says about
   * itself. Reading only the first left the rest as old as the last hourly
   * sweep, which on a page somebody is watching is simply wrong.
   *
   * Both chassis reads yield to operator actions, so a page left open never
   * makes somebody wait to press Reboot.
   */
  /**
   * In a ref, not in the mutation's own flag.
   *
   * The tick below is set up once and closes over the mutation object from that
   * render, and `isPending` on a stale object never changes — so the guard was
   * always true and a new read started every fifteen seconds whether or not the
   * last had finished. A full OMCI read is five commands on one CLI session and
   * takes over twenty seconds on a C300, so they queued: response times of 22s,
   * 33s, 40s, 49s, 58s, climbing for as long as the page stayed open.
   */
  const reading = useRef(false);
  const configReadAt = useRef(0);

  const refreshOptics = useMutation({
    mutationFn: async () => {
      reading.current = true;
      const optics = await api.post<{
        rxPowerDbm: number | null;
        distanceM: number | null;
        warnings?: string[];
      }>(
        `/olts/${encodeURIComponent(onu!.oltId)}/onus/${encodeURIComponent(onu!.id)}/refresh-optics`,
        {},
      );
      /**
       * The ONU's own configuration, on a slower clock than its light.
       *
       * Optics and state are one command and change minute to minute. The
       * configuration is five commands on a C300 and only changes when somebody
       * changes it, so re-reading it every fifteen seconds spent the whole
       * budget on an answer that was already correct. Once a minute, and
       * whenever an operator asks with Summon.
       *
       * Failing it must not lose the optics already in hand.
       */
      if (Date.now() - configReadAt.current > 60_000) {
        configReadAt.current = Date.now();
        await api
          .get(
            `/olts/${encodeURIComponent(onu!.oltId)}/onus/${encodeURIComponent(onu!.id)}/omci-config?refresh=1`,
          )
          .catch(() => undefined);
      }
      return optics;
    },
    onSuccess: async (r) => {
      // The read got through: clear any stale/unreachable banner and stamp the
      // time, so the header can show how fresh the screen is.
      setReadError(null);
      setLastGoodAt(Date.now());
      // No notice on a good read: the figures update on screen, and this runs
      // on a loop — a banner each time would be noise. A chassis that returned
      // nothing is still worth saying out loud.
      if (r.rxPowerDbm === null) {
        notify('The chassis returned no reading — the previous one is kept.');
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['onu', id] }),
        queryClient.invalidateQueries({ queryKey: ['onu', onu!.id, 'omci-config'] }),
        queryClient.invalidateQueries({ queryKey: ['olt', onu!.oltId, 'events'] }),
        // The VLANs and ports the page shows are DB-recorded intent, so they are
        // cheap to re-read and belong in the loop — another operator's change on
        // an open page should not read as this ONU's until a manual refresh.
        queryClient.invalidateQueries({ queryKey: ['onu-config', configBase, 'vlans'] }),
        queryClient.invalidateQueries({ queryKey: ['onu-config', configBase, 'ports'] }),
        queryClient.invalidateQueries({ queryKey: ['onu-config', configBase, 'config-plan'] }),
        // The TR-069 half, when the subscriber has one — the device document
        // AND the WAN card's IPv6 read of it (`wan-ipv6`, an NBI read like
        // the document itself), which used to freeze at its first answer
        // while External IP above it kept moving (review, 2026-09-13).
        deviceId
          ? queryClient.invalidateQueries({ queryKey: ['device', deviceId] })
          : Promise.resolve(),
        deviceId
          ? queryClient.invalidateQueries({ queryKey: wanIpv6QueryScope(configBase) })
          : Promise.resolve(),
      ]);
      await opticsQuery.refetch();
    },
    // A failed read keeps the last-known values on screen; the header carries a
    // lasting "chassis unreachable" banner instead of a toast that flashes past
    // on every loop tick.
    onError: (err) => setReadError(err instanceof Error ? err.message : 'Optical refresh failed'),
    // Cleared however it ended, or the page never reads again.
    onSettled: () => {
      reading.current = false;
    },
  });

  /**
   * Push what SimpleACS holds onto the ONU, without taking it apart.
   *
   * The difference from Recreate is the whole point. A recreate deauthorises
   * the ONU and builds it again, which drops the subscriber for as long as that
   * takes; this re-states their VLANs and ports over whatever is on the chassis
   * now. A setting that did not land lands; a subscriber who is already correct
   * notices nothing.
   */
  /**
   * Ask the ONU what it is, now.
   *
   * The read half, and the exact opposite of Resync. Everything the page shows
   * about the ONU itself comes from one of three places — the chassis for its
   * optics and state, the chassis again for the service it carries, and the ACS
   * for what the ONU says about itself — so all three are asked and the top of
   * the page redraws from the answers.
   *
   * The page already does this on a fifteen-second loop while it is open. This
   * is the same thing on demand, plus a full TR-069 parameter walk, which the
   * loop deliberately does not do: that is a connection request and a walk of
   * the whole data model, which is worth doing when somebody asks and not every
   * fifteen seconds.
   */
  const summon = useMutation({
    mutationFn: async () => {
      reading.current = true;
      const base = `/olts/${encodeURIComponent(onu!.oltId)}/onus/${encodeURIComponent(onu!.id)}`;
      // Optics and state, then the ONU's own configuration. Neither failing
      // should stop the other, or the TR-069 walk below.
      await api.post(`${base}/refresh-optics`, {}).catch(() => undefined);
      await api.get(`${base}/omci-config?refresh=1`).catch(() => undefined);
      if (deviceId) {
        // The ACS's own summon: a connection request and a walk of the objects
        // worth re-reading, which the device action already knows.
        await api
          .post(`/devices/${encodeURIComponent(deviceId)}/actions`, { action: 'summon' })
          .catch(() => undefined);
      }
    },
    onSuccess: async () => {
      notify(`Read ${onu!.serial} from ${deviceId ? 'the OLT and the ACS' : 'the OLT'}.`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['onu', id] }),
        queryClient.invalidateQueries({ queryKey: ['onu', onu!.id, 'omci-config'] }),
        deviceId
          ? queryClient.invalidateQueries({ queryKey: ['device', deviceId] })
          : Promise.resolve(),
        // The walk re-read the WAN's IPv6 leaves too.
        deviceId
          ? queryClient.invalidateQueries({ queryKey: wanIpv6QueryScope(configBase) })
          : Promise.resolve(),
      ]);
      await opticsQuery.refetch();
    },
    onError: (err) => notify(err instanceof Error ? err.message : 'Could not read the ONU', 'danger'),
    onSettled: () => {
      reading.current = false;
    },
  });

  const [resyncOpen, setResyncOpen] = useState(false);
  const tasks = useTasks();
  /**
   * Resync, as BACKGROUND work.
   *
   * It opens an SSH session and re-states a subscriber's whole config, which
   * on a busy chassis is minutes behind a queue. Holding the dialog open for
   * that pinned the operator to this page for something they had already
   * decided — and made the outcome hostage to the dialog, since navigating
   * away lost the answer while the platform carried on writing. The confirm
   * closes immediately now and the toast reports what the chassis said.
   */
  const startResync = (opts: ResyncApplyOptions = {}) => {
    const target = onu!;
    setResyncOpen(false);
    tasks.start<CliActionResult>({
      label: `Resync ${target.serial}`,
      href: onuPath(target),
      // The route now enqueues the chassis write and answers a handle; poll it
      // to completion so a slow resync never 504s at the proxy. An upstream
      // speed profile rides along when the review asked for one — the bare
      // interface Reprovision used to exist for.
      run: () =>
        api
          .post<JobHandle>(
            `/olts/${encodeURIComponent(target.oltId)}/onus/${encodeURIComponent(target.id)}/resync`,
            { ...(opts.upstreamProfileId ? { upstreamProfileId: opts.upstreamProfileId } : {}), ...(opts.reboot ? { reboot: true } : {}) },
          )
          .then((handle) => runJob<CliActionResult>(handle)),
      done: (r) =>
        r.warnings?.length
          ? `${target.serial} re-stated on the OLT, with warnings — open it to read them.`
          : `${target.serial} now has SimpleACS's settings on the OLT.`,
      invalidate: [
        ['onu', id],
        ['onu', target.id, 'omci-config'],
        ['onu-config', configBase],
      ],
    });
  };

  /**
   * Reboot through the OLT rather than through the ACS.
   *
   * The chassis can reboot any ONU it configures, so this is offered to every
   * one of them — and for an ONU that is not on TR-069 it is the only way.
   * Dry-run first, because a reboot costs the subscriber their service for a
   * minute and the operator should see that said out loud before it happens.
   */
  const chassisReboot = useMutation({
    mutationFn: (dryRun: boolean) =>
      api.post<CliActionResult>(
        `/olts/${encodeURIComponent(onu!.oltId)}/onus/${encodeURIComponent(onu!.id)}/reboot`,
        { dryRun },
      ),
    onSuccess: async (r, dryRun) => {
      if (dryRun) {
        setRebootPlan(r);
        return;
      }
      setRebootPlan(null);
      notify(`${onu!.serial} is rebooting — it re-ranges in about a minute.`);
      await queryClient.invalidateQueries({ queryKey: ['onu', id] });
    },
    onError: (err) => notify(err instanceof Error ? err.message : 'Reboot failed', 'danger'),
  });

  /**
   * The ONU's admin-state ON THE CHASSIS — the OLT's own line lock — read live
   * so the toggle reflects the chassis, not a stored guess. `enabled: null` (no
   * SNMP community, or the leaf is unreadable) hides the control rather than
   * showing a wrong position. Polled on the same gentle cadence as the page.
   */
  const adminStateQuery = useQuery({
    queryKey: ['onu-admin-state', onu?.oltId, onu?.id],
    queryFn: () =>
      api.get<{ enabled: boolean | null }>(
        `/olts/${encodeURIComponent(onu!.oltId)}/onus/${encodeURIComponent(onu!.id)}/admin-state`,
      ),
    enabled: Boolean(onu) && (olt?.vendor === 'zte' || olt?.vendor === 'huawei') && can('onu.provision'),
    refetchInterval: 30_000,
  });

  /**
   * The chassis line lock's two calls, shared by the combined Disable dialog
   * (preview + write) and the one-click Enable below. The admin-state read
   * above says whether the chassis can be asked at all: `enabled === null`
   * means it cannot, and only the SimpleACS half runs.
   */
  const adminPath = `/olts/${encodeURIComponent(onu?.oltId ?? '')}/onus/${encodeURIComponent(
    onu?.id ?? '',
  )}/admin-state`;
  const chassisLine: boolean | null = adminStateQuery.data?.enabled ?? null;

  /**
   * Where the in-service switch is thrown, in both directions.
   *
   * Built from `onu?` rather than `onu!` because the hooks all run before the
   * loading guard below; nothing can call it until the row exists, and the
   * page has no button for it until then either.
   */
  const disableEndpoint = `/olts/${encodeURIComponent(
    onu?.oltId ?? '',
  )}/onus/${encodeURIComponent(onu?.id ?? '')}/disabled`;
  /**
   * One switch, both halves (operator, 2026-09-09: "combine disable line and
   * disable"). Disable = out of service in SimpleACS AND the line deactivated
   * on the chassis; Enable = both back. The chassis half rides only where the
   * OLT reports the line state; where it cannot be read, the SimpleACS half
   * runs alone and the dialog says so. The direction is whichever half is
   * currently off: a line somebody deactivated by hand reads as Disabled here
   * too, and Enable puts both right.
   */
  const isDisabled = Boolean(onu?.disabledAt) || chassisLine === false;
  const startEnable = () => {
    if (!onu) return;
    const target = onu;
    const lineOff = chassisLine === false;
    tasks.start<unknown>({
      label: `Enable ${target.serial}`,
      href: onuPath(target),
      run: async () => {
        await api.post(disableEndpoint, { disabled: false });
        if (lineOff) await api.post<CliActionResult>(adminPath, { enable: true, dryRun: false });
        return null;
      },
      done: () =>
        lineOff
          ? `${target.serial} is back in service here and its line is re-enabled on ${olt?.name ?? 'the OLT'} — it re-ranges in about a minute.`
          : `${target.serial} is back in service in SimpleACS.`,
      invalidate: [['olt'], ['olts'], ['onu'], ['onus'], ['onu-admin-state', target.oltId, target.id]],
    });
  };

  /**
   * The whole page on a fifteen-second loop while somebody is looking at it.
   *
   * Each pass is CLI sessions against the chassis, so it runs only while the
   * tab is visible: the countdown stops when it is hidden and resumes when it
   * comes back. A pass still in flight is never stacked on — the timer waits
   * for it rather than queueing another session behind it.
   */
  /**
   * While a chassis write is running, re-read this ONU every two seconds. It is
   * the only time this page has a reason to poll itself, and it stops as soon
   * as the row settles — an operator should not have to refresh to find out
   * whether the subscriber they just created exists.
   */
  useEffect(() => {
    if (onu?.provisioningStatus !== 'running') return;
    const t = setInterval(() => void queryClient.invalidateQueries({ queryKey: ['onu', id] }), 2000);
    return () => clearInterval(t);
  }, [onu?.provisioningStatus, id, queryClient]);

  const OPTICS_EVERY = 15;
  const [opticsIn, setOpticsIn] = useState(OPTICS_EVERY);
  useEffect(() => {
    if ((olt?.vendor !== 'zte' && olt?.vendor !== 'huawei') || !onu) return;
    const tick = setInterval(() => {
      if (document.hidden) return;
      setOpticsIn((s) => {
        if (s > 1) return s - 1;
        // The ref, not the mutation's flag: this closure is from the first
        // render and that flag never moves.
        if (!reading.current) refreshOptics.mutate();
        return OPTICS_EVERY;
      });
    }, 1000);
    return () => clearInterval(tick);
    // Re-subscribing on every pending flip would restart the countdown
    // mid-read, so the effect deliberately does not depend on the mutation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [olt?.vendor, onu?.id]);


  if (query.isLoading) {
    return (
      <div className="flex justify-center p-16">
        <Spinner className="size-6" />
      </div>
    );
  }
  if (query.error) return <ErrorNotice error={query.error} />;
  if (!onu || !olt) return <EmptyState title="ONU not found" />;

  /**
   * Everything that acts on this ONU, as one list rather than a row of JSX.
   *
   * The same actions have to appear in two places — across the top on a desktop,
   * where there is room for them beside the page title, and in a footer bar on a
   * phone, where a row of eight buttons above the reading an engineer came for
   * is most of the screen. Describing them once and rendering them twice is what
   * keeps the two from drifting apart.
   */
  const chassisActions = can('onu.provision') && (olt.vendor === 'zte' || olt.vendor === 'huawei');
  /**
   * Ordered by how often day-to-day support reaches for each one (operator,
   * 2026-09-09): read the ONU, bounce it, re-state it, replace a dead box,
   * change how the subscriber is served, the chassis line lock — then the
   * setup-time controls, then the destructive tail. Every gate and every
   * behaviour is the one it had; only the order changed. The phone header
   * still folds `danger` items behind More.
   */
  const actions: OnuAction[] = [
    // Summon reads, Resync writes — two buttons on purpose (the one button
    // used to do opposite things to two subscribers side by side). Summon is
    // offered for every ONU: most of this fleet is OMCI and the chassis
    // answers for all of it.
    {
      key: 'summon',
      label: 'Summon',
      icon: <RefreshCw className="size-4" />,
      loading: summon.isPending,
      onClick: () => summon.mutate(),
      title: "Read this ONU — optics, state, its configuration, and the ACS's view of it. Changes nothing.",
    },
    ...(can('onu.provision')
      ? [
          {
            key: 'reboot',
            label: 'Reboot',
            icon: <Power className="size-4" />,
            loading: chassisReboot.isPending,
            onClick: () => chassisReboot.mutate(true),
            title: 'Reboot this ONU through the OLT',
          },
          {
            key: 'resync',
            label: 'Resync',
            icon: <RotateCw className="size-4" />,
            loading: false,
            // Opens the review; nothing reaches the chassis until Apply there.
            onClick: () => setResyncOpen(true),
            title: "Review and re-state SimpleACS's settings onto the ONU — nothing is removed without saying so first",
          },
        ]
      : []),
    // How the subscriber is served, then how the ONU is managed, side by side
    // (operator, 2026-09-09: "put mngt next to wan").
    ...(chassisActions
      ? [
          {
            key: 'wan',
            label: 'WAN',
            icon: <Router className="size-4" />,
            onClick: () => setWanOpen(true),
            title: "Bridging or routing — who terminates the subscriber's connection",
          },
          {
            key: 'mode',
            label: 'Mngt',
            icon: <Settings2 className="size-4" />,
            onClick: () => setModeOpen(true),
            title: 'How this ONU is managed: TR-069 or OMCI, its management VLAN, addressing and reachability',
          },
        ]
      : []),
    ...(can('device.write')
      ? [
          {
            key: 'preset',
            label: 'Preset',
            icon: <SlidersHorizontal className="size-4" />,
            onClick: () => setPresetOpen(true),
            title: "Apply a saved device preset — the ONU's own WiFi, WAN and LAN — to this subscriber",
          },
        ]
      : []),
    // The replacement job, after the setup buttons (operator: "move swap to
    // after preset").
    ...(can('onu.provision')
      ? [
          {
            key: 'swap',
            label: 'Swap',
            icon: <Replace className="size-4" />,
            onClick: () => setSwapOpen(true),
            title:
              "Replace this ONU with an unauthorised unit waiting on the OLT — it takes over the subscriber's slot and settings",
          },
        ]
      : []),
    /*
      One switch (operator, 2026-09-09: "combine disable line and disable").
      Disable takes the ONU out of service in SimpleACS AND deactivates its line
      on the chassis where the OLT reports the line state; Enable puts both
      back. Red and green because that is what those colours mean everywhere
      else here — and now the red IS a cut-off, which the dialog says in as
      many words. `danger` keeps Disable off the phone's inline bar, behind
      More with Delete.
    */
    ...(can('onu.provision')
      ? [
          isDisabled
            ? {
                key: 'enable',
                label: 'Enable',
                icon: <CircleCheck className="size-4" />,
                success: true,
                onClick: startEnable,
                title:
                  chassisLine === false
                    ? 'Put this ONU back in service in SimpleACS and re-enable its line on the OLT'
                    : 'Put this ONU back in service in SimpleACS — bulk runs match it again and writes to it are accepted',
              }
            : {
                key: 'disable',
                label: 'Disable',
                icon: <PowerOff className="size-4" />,
                danger: true,
                onClick: () => setDisabling(true),
                title:
                  chassisLine === null
                    ? 'Stop SimpleACS acting on this ONU. The OLT does not report its line state, so the line itself is left as it is.'
                    : 'Stop SimpleACS acting on this ONU and deactivate its line on the OLT — the subscriber goes offline until it is enabled again',
              },
          /*
            Just the router, back to factory settings (operator, 2026-10-08:
            "can add a reset button to reset just the onu to factory
            settings?"). Its place on the OLT and its record stay; the
            platform puts the recorded WAN and WiFi back when it checks in.
            Red, behind More on the phone, like the other destructive ones.
          */
          {
            key: 'factory-reset',
            label: 'Factory reset',
            icon: <RotateCcw className="size-4" />,
            danger: true,
            onClick: () => setOltAction('factory-reset'),
            title:
              "Reset the router to factory settings — the platform puts its recorded WAN and WiFi back when it checks in",
          },
          {
            key: 'recreate',
            label: 'Recreate',
            icon: <Eraser className="size-4" />,
            onClick: () => setOltAction('recreate'),
            title: "Clear the ONU on the chassis and put SimpleACS's settings back on it",
          },
          {
            key: 'delete',
            label: 'Delete',
            icon: <Trash2 className="size-4" />,
            onClick: () => setOltAction('deauthorize'),
            title: 'Remove the ONU authorisation from the OLT',
            danger: true,
          },
        ]
      : []),
  ];


  return (
    <>
      <button
        onClick={() => navigate('/onus')}
        className="mb-4 inline-flex items-center gap-2 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" />
        Back to ONUs
      </button>

      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-semibold text-ink">{onu.name ?? onu.serial}</h1>
            {can('onu.provision') && (
              <button
                onClick={() => setRenaming(true)}
                className="text-muted transition-colors hover:text-ink"
                title="Rename — written to the chassis itself"
                aria-label="Rename this ONU"
              >
                <Pencil className="size-4" />
              </button>
            )}
            <Pill state tone={onuHealthTone(onu)} dot>
              {onuHealthLabel(onu)}
            </Pill>
            {/*
              Three states, because two could not tell the difference that
              matters. `tr069_other` is an ONU configured for TR-069 and pointed
              at an ACS this organisation does not run: it looks managed and is
              not, and only OMCI reaches it. Twelve ONUs on this fleet are in
              exactly that state.

              `managementState` is absent until a chassis read has happened, and
              the fallback is the old question — does a device record exist —
              rather than a guess, because "we have not looked" must not render
              as "OMCI".
            */}
            <Pill state
              tone={
                onu.oltAcsMismatch
                  ? 'warn'
                  : displayMode === 'tr069'
                    ? 'primary'
                    : displayMode === 'tr069_other'
                      ? 'warn'
                      : 'neutral'
              }
            >
              {displayMode === 'omci' ? (
                <>
                  <CloudOff className="size-3" /> OMCI
                </>
              ) : (
                <>
                  <Cloud className="size-3" />{' '}
                  {displayMode === 'tr069_other' ? 'TR-069 (other)' : 'TR-069'}
                </>
              )}
            </Pill>
            {/* The OLT and the ACS disagree — informing our ACS while the OLT's
                own config carries no ACS line. Flagged next to the mode, with the
                fix in the banner below. */}
            {onu.oltAcsMismatch ? (
              <Pill tone="warn">
                <CloudOff className="size-3" /> OLT config
              </Pill>
            ) : null}
            {/*
              Configured for our ACS, and not reaching it.

              The pill above says what the CHASSIS was told, and it says TR-069
              whether or not the CPE ever answered — which is honest and, on its
              own, misleading: an ONU that was put on TR-069 and never informed
              looks identical here to one informing every five minutes. This is
              the other half of that fact, and it is the half somebody can act
              on.
            */}
            {(onu.tr069Reach === 'silent' && !informGraceOpen) || onu.tr069Reach === 'stale' ? (
              <Pill state tone={onu.tr069Reach === 'silent' ? 'danger' : 'warn'}>
                <CloudOff className="size-3" />{' '}
                {onu.tr069Reach === 'silent' ? 'never informed' : 'stopped informing'}
              </Pill>
            ) : null}
            {/* Next to `state`, which is what the chassis reported, and saying
                something the chassis has no opinion about: a person took this
                subscriber out of SimpleACS's reach. A `working` ONU can carry
                this pill and usually does. */}
            {onu.disabledAt ? (
              <DisabledPill kind="onu" disabledReason={onu.disabledReason} />
            ) : null}
          </div>
          <p className="mt-1 text-sm text-muted">
            {onu.serial} · {onu.interfaceName ?? `${onu.ponPort}:${onu.onuIndex}`} on {olt.name}
          </p>

          {/*
            The shortlist, where the fault is.

            A status by itself sends somebody to go and think; the point of
            naming this state was being able to act on it, so the causes travel
            with it in the order the fleet's own history puts them. Silent and
            stale get DIFFERENT lists on purpose: for a stale ONU the management
            path demonstrably worked, so opening with "check the management
            VLAN" would send an engineer to look at the one thing that is fine.
          */}
          {(onu.tr069Reach === 'silent' && !informGraceOpen) || onu.tr069Reach === 'stale' ? (
            <div
              className={`mt-3 rounded-xl border p-3 text-sm ${
                onu.tr069Reach === 'silent'
                  ? 'border-danger/30 bg-danger-soft'
                  : 'border-warn/30 bg-warn-soft'
              }`}
            >
              <p className="font-medium text-ink">
                {onu.tr069Reach === 'silent'
                  ? 'The chassis has this ONU on TR-069, but it has never reached the ACS.'
                  : 'This ONU reached the ACS before and has not lately.'}
                {onu.lastInformAt ? (
                  <span className="font-normal text-muted">
                    {' '}
                    Last inform {formatRelative(onu.lastInformAt)}.
                  </span>
                ) : null}
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-muted">
                {(onu.tr069Reach === 'silent' ? TR069_SILENCE_CAUSES : TR069_STALE_CAUSES).map(
                  (cause) => (
                    <li key={cause}>{cause}</li>
                  ),
                )}
              </ul>
            </div>
          ) : null}

          {/*
            The OLT and the ACS disagree.

            The CPE is informing our ACS right now — so this subscriber IS on our
            TR-069 — but the OLT's own config carries no `tr069-mgmt … acs …` line
            for it, so the chassis view alone reads OMCI. That is the bridged
            shape: the CPE informs over its WAN, not the OLT-delivered path. The
            fix is to write the OLT config to match (a resync), unless the ONU is
            deliberately bridged, in which case the two are meant to differ.
          */}
          {onu.oltAcsMismatch ? (
            <div className="mt-3 rounded-xl border border-warn/30 bg-warn-soft p-3 text-sm">
              <p className="font-medium text-ink">
                This ONU is informing our ACS, but {olt.name}’s own config has no TR-069
                management line for it — the OLT and the ACS disagree.
                {onu.lastInformAt ? (
                  <span className="font-normal text-muted">
                    {' '}
                    Last inform {formatRelative(onu.lastInformAt)}.
                  </span>
                ) : null}
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-muted">
                <li>
                  Resync this ONU to write the OLT’s TR-069 management line, so the chassis
                  config matches how it is actually managed.
                </li>
                <li>
                  Leave it as is if the subscriber is deliberately bridged — the CPE informs
                  over its own WAN, and the OLT having no ACS line is then correct.
                </li>
              </ul>
            </div>
          ) : null}

          {/* What a person wrote about this subscriber, with the rest of what
              identifies them — the name, the serial, where they sit. It reads
              before anything the chassis says because it is usually the reason
              somebody opened the page: "prepaid, cuts himself off", "gate code
              4417". SmartOLT has held one of these per ONU for the life of this
              fleet and the import fills them in; until now nothing showed them.

              Bounded on purpose. A note is whatever a person typed — it can be
              a paragraph, a pasted log, or one 900-character word — so it is
              width-capped, wrapped mid-word if it has to be, and scrolls inside
              its own box rather than pushing the page around. It is text in a
              text node: React escapes it, and nothing here renders HTML. */}
          {(onu.notes ?? '').trim() !== '' ? (
            <div className="mt-2.5 flex max-w-2xl items-start gap-2 rounded-lg border border-line bg-surface-2 px-3 py-2">
              <StickyNote className="mt-0.5 size-3.5 shrink-0 text-muted" />
              <p className="max-h-28 flex-1 overflow-y-auto whitespace-pre-wrap break-words text-sm text-ink">
                {onu.notes}
              </p>
              {can('onu.provision') && (
                <button
                  onClick={() => setNoting(true)}
                  className="shrink-0 text-muted transition-colors hover:text-ink"
                  title="Edit this note — kept in SimpleACS only, never sent to the OLT"
                  aria-label="Edit the note on this ONU"
                >
                  <Pencil className="size-3.5" />
                </button>
              )}
            </div>
          ) : (
            can('onu.provision') && (
              <button
                onClick={() => setNoting(true)}
                className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted transition-colors hover:text-ink"
                title="Write a note about this subscriber — kept in SimpleACS only, never sent to the OLT"
              >
                <StickyNote className="size-3.5" />
                Add a note
              </button>
            )
          )}
        </div>

        {/* Beside the title where there is room for them. Below `sm` this is
            hidden and the same actions come back as a footer bar, because eight
            buttons stacked above the reading an engineer came for is most of a
            phone screen. */}
        <div className="hidden flex-wrap items-center justify-end gap-2 sm:flex">
          {actions.map((a) => (
            <Button
              key={a.key}
              variant={a.danger ? 'danger' : a.success ? 'success' : 'secondary'}
              icon={a.icon}
              loading={a.loading}
              onClick={a.onClick}
              title={a.title}
            >
              {a.label}
            </Button>
          ))}
        </div>
      </div>

      {/* First of the notices, because it is the one that explains the others
          being ignored: every write on this page is refused while it stands,
          and no bulk run will touch this subscriber. */}
      <DisabledBanner
        kind="onu"
        name={onu.serial}
        disabledAt={onu.disabledAt}
        disabledReason={onu.disabledReason}
      />

      {/* Configured for TR-069, and pointed at somebody else's ACS.

          The state that needed a name. Such an ONU looks managed and is not:
          it will never appear in our ACS, every TR-069 control on this page is
          inert on it, and the only thing that reaches it is OMCI through its
          OLT. Said at the top rather than left to the pill, because the cost of
          missing it is sending a setting to a device that is not ours to
          configure and watching nothing happen. */}
      {managementState === 'tr069_other' && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-warn/30 bg-warn-soft px-4 py-3 text-sm text-warn">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span>
            This ONU is on TR-069, but it informs an ACS this organization does not run
            {onu.acsUrl ? (
              <>
                {' '}
                — <code className="font-mono text-xs">{onu.acsUrl}</code>
              </>
            ) : null}
            . Nothing sent over TR-069 will reach it. It can still be managed over OMCI through{' '}
            {olt.name}: the WiFi, WAN and VLAN controls that write to the chassis work as normal,
            and anything that needs the ACS does not.
          </span>
        </div>
      )}

      {/* Between asking for TR-069 and the ONU answering.

          An ONU informs on its own schedule, so there is a gap of up to one
          inform interval where the chassis has been written and the ACS has
          heard nothing. The page used to say "OMCI" through all of it, which
          reads as though the write failed — and an operator who re-runs a
          write that worked is how an ONU ends up half-configured. */}
      {onu.managementMode === 'tr069' && !deviceId && informGraceOpen && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-info/30 bg-info-soft px-4 py-3 text-sm text-info">
          <Loader2 className="size-4 shrink-0 animate-spin" />
          <span>
            <span className="font-medium">Waiting for {onu.serial} to inform.</span> TR-069 was
            written to the chassis{' '}
            {onu.managementSetAt ? formatRelative(onu.managementSetAt) : 'recently'} and the ONU has
            not called the ACS yet. It reports on its own schedule — usually within a few minutes of
            a reboot, otherwise at its next periodic inform. Nothing more to do here.
          </span>
        </div>
      )}

      {/* And the other direction, which is a genuine fault rather than a wait:
          the operator locked TR-069 off and the ONU is still calling home. */}
      {onu.managementMode === 'omci' && deviceId && device?.lastInform &&
        onu.managementSetAt &&
        device.lastInform > onu.managementSetAt && (
          <div className="mb-4 rounded-lg border border-warn/30 bg-warn-soft px-4 py-3 text-sm text-warn">
            <span className="font-medium">Set to OMCI, but still informing.</span> {onu.serial} was
            taken off TR-069 {formatRelative(onu.managementSetAt)} and has informed to the ACS since
            — {formatRelative(device.lastInform)}. The lock did not take, or another system put it
            back. Read the ONU with Summon and check its management mode.
          </div>
        )}

      {/* An ONU the chassis no longer configures. Its history is kept, because
          the optical trend and state changes are the record of a subscriber who
          existed — and a swapped unit that comes back clears this by itself on
          the next sweep, so there is nothing to press. */}
      {onu.removedAt && (
        <div className="mb-4 rounded-lg border border-warn/30 bg-warn-soft px-4 py-3 text-sm text-warn">
          <span className="font-medium">No longer on the chassis.</span>{' '}
          {formatTimestamp(onu.removedAt)} was the last sweep that found{' '}
          {olt.name} still configuring {onu.serial} at {onu.ponPort}:{onu.onuIndex}. Everything
          below is the last thing known about it. If the ONU is put back on this port it will
          reappear on its own; if it was replaced, the Swaps tab names the unit that took over.
        </div>
      )}

      {/* An authorise that is still running, or one that did not land.
          The row is written before the chassis is touched so there is a page to
          land on, which means this page has to be honest about which of the
          three states it is showing. */}
      {onu.provisioningStatus === 'running' && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-info/30 bg-info-soft px-4 py-3 text-sm text-info">
          <Loader2 className="size-4 shrink-0 animate-spin" />
          <span>
            <span className="font-medium">Writing this ONU to {olt.name}.</span> It takes a few
            seconds, longer if something else is already running on the chassis. This page updates
            itself.
          </span>
        </div>
      )}
      {onu.provisioningStatus === 'failed' && (
        <div className="mb-4 rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
          <span className="font-medium">The OLT did not accept this ONU.</span>{' '}
          {onu.provisioningMessage ?? 'No reason was recorded.'} Nothing is configured on{' '}
          {olt.name} for {onu.serial}; fix the cause and authorise it again, or delete this record.
        </div>
      )}

      {/* The deferred half of a resync pushed while this ONU was offline. The
          write landed on the OLT; whether the ONU has TAKEN it is unknown until
          it ranges. `waiting` holds for 24h; a unit that never returns falls to
          `failed` — a standing "needs resync" the operator must clear. */}
      {onu.pendingConfirmation?.status === 'waiting' && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-info/30 bg-info-soft px-4 py-3 text-sm text-info">
          <Loader2 className="size-4 shrink-0 animate-spin" />
          <span>
            <span className="font-medium">
              Config pushed — waiting for {onu.serial} to come back online.
            </span>{' '}
            {olt.name} has the new configuration and applies it when the ONU next reports in. Held
            until {formatTimestamp(onu.pendingConfirmation.expiresAt)}, then flagged as needing a
            resync. This page updates itself.
          </span>
        </div>
      )}
      {onu.pendingConfirmation?.status === 'failed' && (
        <div className="mb-4 rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
          <span className="font-medium">Needs a resync.</span> Config was pushed to {onu.serial}{' '}
          while it was offline, but it did not come back online within 24h to confirm it took.
          Resync it once it is back so its configuration is known to be in place.
        </div>
      )}
      {/* WiFi set while the ONU was offline, waiting on its next inform (or just
          landed). Its own store, resolved by the CPE informing — not the OLT
          sweep — so it lives beside, not inside, the resync banner above. */}
      {onu.wifiPending?.status === 'waiting' && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-warn/30 bg-warn-soft px-4 py-3 text-sm text-warn">
          <Loader2 className="size-4 shrink-0 animate-spin" />
          <span>
            <span className="font-medium">WiFi queued.</span> {onu.wifiPending.summary} This page
            updates itself.
          </span>
        </div>
      )}
      {onu.wifiPending?.status === 'applied' && (
        <div className="mb-4 rounded-lg border border-success/30 bg-success-soft px-4 py-3 text-sm text-success">
          <span className="font-medium">WiFi applied.</span> {onu.wifiPending.summary}
        </div>
      )}
      {/* The recorded WAN mode, queued for a CPE just linked to this ONU (a
          first link, or the replacement after a Swap). Same store and lifecycle
          as the WiFi banner above — resolved by the CPE informing. */}
      {onu.wanPending?.status === 'waiting' && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-warn/30 bg-warn-soft px-4 py-3 text-sm text-warn">
          <Loader2 className="size-4 shrink-0 animate-spin" />
          <span>
            <span className="font-medium">WAN mode queued for the new CPE.</span>{' '}
            {onu.wanPending.summary} This page updates itself.
          </span>
        </div>
      )}
      {onu.wanPending?.status === 'applied' && (
        <div className="mb-4 rounded-lg border border-success/30 bg-success-soft px-4 py-3 text-sm text-success">
          <span className="font-medium">WAN mode settled.</span> {onu.wanPending.summary}
        </div>
      )}
      {onu.dormant && onu.pendingConfirmation?.status !== 'waiting' && (
        <div className="mb-4 rounded-lg border border-line bg-surface-2 px-4 py-3 text-sm text-muted">
          <span className="font-medium">Dormant.</span>{' '}
          {onu.lastSeenAt
            ? `Offline since it was last seen ${formatRelative(onu.lastSeenAt)} — long enough to be treated as dormant, likely a subscriber who left rather than a fresh outage.`
            : 'Never seen online — long enough to be treated as dormant rather than a fresh outage.'}
        </div>
      )}

      {notice && (
        <div
          className={`mb-4 rounded-lg border px-4 py-2.5 text-sm ${
            noticeTone === 'danger'
              ? 'border-danger/30 bg-danger-soft text-danger'
              : 'border-info/30 bg-info-soft text-info'
          }`}
        >
          {notice}
        </div>
      )}

      <div className="mb-5 flex gap-1 overflow-x-auto border-b border-line">
        {/* Configs shows for every ONU, whichever way it is managed: on TR-069
            it edits the CPE directly, on OMCI it edits what the OLT can carry —
            so unlike the old TR-069 tab it is never empty and never hidden. */}
        {TABS.map(([value, label]) => {
          // Configs wears the PON section's own colour — the one the ONUs nav
          // item is drawn in — marking the tab where this platform WRITES to the
          // ONU, as opposed to the read-only chassis views on the other tabs.
          const accent = value === 'configs' ? ONU_ACCENT : undefined;
          const active = activeTab === value;
          return (
            <button
              key={value}
              onClick={() => setTab(value)}
              style={
                accent
                  ? { color: accent, borderColor: active ? accent : 'transparent' }
                  : undefined
              }
              className={
                active
                  ? `whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium ${
                      accent ? '' : 'border-primary text-primary'
                    }`
                  : `whitespace-nowrap border-b-2 border-transparent px-4 py-2.5 text-sm ${
                      accent ? 'opacity-80 hover:opacity-100' : 'text-muted hover:text-ink'
                    }`
              }
            >
              {label}
            </button>
          );
        })}
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-5">
          {/* State and the light together, state first: "is it up" is asked
              before anything else, and the answer changes how the rest reads —
              a receive level on an offline ONU is the last one anybody saw
              rather than a measurement of now. */}
          {/* Above everything, because everything below refreshes on it —
              settings, light, state and the ACS half alike. Sitting in the
              light card's header it read as a countdown for that one card. */}
          {(olt.vendor === 'zte' || olt.vendor === 'huawei') && (
            <div className="flex items-center justify-end gap-2 text-xs">
              {readError ? (
                // The chassis did not answer the last read: last-known values are
                // still on screen, and this says so plainly and lastingly, with
                // how old the good data is, rather than a toast that scrolls away.
                <span
                  className="inline-flex items-center gap-1.5 rounded-full border border-warn/30 bg-warn-soft px-2.5 py-0.5 text-warn"
                  title={readError}
                >
                  <AlertTriangle className="size-3.5" />
                  Chassis unreachable — showing last-known
                  {lastGoodAt ? <> from {formatRelative(lastGoodAt)}</> : null}
                </span>
              ) : (
                <span className="flex items-center gap-2 text-faint">
                  <Activity
                    className={`size-3.5 ${refreshOptics.isPending ? 'animate-pulse text-primary' : ''}`}
                  />
                  {refreshOptics.isPending ? (
                    'reading the ONU…'
                  ) : (
                    <>
                      next read in <span className="tabular-nums">{opticsIn}s</span>
                    </>
                  )}
                </span>
              )}
            </div>
          )}

          {/* How it is doing, beside where it lives on the chassis. "Is it up"
              is asked before any other question, and the next one is always
              "which port" — so the two that answer those sit together. */}
          <MatchedPair>
            <OpticsCard
            state={onuHealthLabel(onu)}
            stateTone={onuHealthTone(onu)}
            lastSeenAt={onu.lastSeenAt}
            uptime={onu.uptime}
            lastOfflineAt={onu.lastOfflineAt ?? null}
            lastOfflineReason={onu.lastOfflineReason ?? null}
            configState={onu.configState ?? null}
            rxPowerDbm={onu.rxPowerDbm}
            txPowerDbm={onu.txPowerDbm}
            oltRxPowerDbm={onu.oltRxPowerDbm}
            distanceM={onu.distanceM}
            temperatureC={onu.temperatureC}
            voltageV={onu.voltageV}
            biasMa={onu.biasMa}
            reading={refreshOptics.isPending}
            trend={
              <OpticalTrend history={opticsQuery.data} loading={opticsQuery.isLoading} embedded />
            }
            />
            <Card
              title="OLT"
              className="flex flex-col"
              bodyClassName="flex flex-1 flex-col"
              action={
                <ChassisRefresh
                  oltId={onu.oltId}
                  onuId={onu.id}
                  label="the chassis view"
                  optics
                  invalidate={[['onu', id], ['onu-optics', id]]}
                  onNotice={notify}
                />
              }
            >
              {/* The rows grow evenly into whatever height the General card
                  sets, so the leftover space becomes a little air in every row
                  instead of one void under the link — which is what the
                  operator circled. */}
              <dl className="flex flex-1 flex-col">
                {/* Only what the chassis actually told us. A column of dashes
                    reads as "we asked and got nothing", which is not what an
                    absent line profile means on an ONU that never had one —
                    and it buries the handful of facts that are there. */}
                {(
                  [
                    ['OLT', olt.name],
                    ['PON port', onu.ponPort],
                    ['ONU id', onu.onuIndex],
                    ['Model', onu.onuType],
                    ['Firmware', device?.softwareVersion ?? null],
                    // The board revision beside the image it runs — on a Tenda HG3 they
                    // are v1.0 and v2.0.0 and get mistaken for each other.
                    ['Hardware', device?.hardwareVersion ?? null],
                    ['Line profile', onu.lineProfile],
                    ['Service profile', onu.serviceProfile],
                    ['Zone', onu.zoneName],
                    [
                      'Splitter',
                      onu.splitterName && lightPath
                        ? // The whole light path, read upstream: FAT → enclosure
                          // → ODF, then the port feeding the line — one fact
                          // instead of three lookups when tracing a fault.
                          [...lightPath.stages.map((st) => st.name)].join(' → ') +
                          (lightPath.feedPonPort ? ` → ${lightPath.feedPonPort}` : '')
                        : onu.splitterName,
                    ],
                    ['Authorised', onu.authorizedAt ? formatTimestamp(onu.authorizedAt) : null],
                    ['Inventory updated', formatTimestamp(onu.updatedAt)],
                  ] as Array<[string, string | null | undefined]>
                )
                  .filter((entry): entry is [string, string] => Boolean(entry[1]))
                  .map(([label, value]) => (
                  <div
                    key={label}
                    className="flex flex-1 items-center justify-between gap-4 border-b border-line px-5 py-1"
                  >
                    <dt className="text-sm text-muted">{label}</dt>
                    <dd className="truncate font-mono text-xs text-ink">{value}</dd>
                  </div>
                ))}
              </dl>
              <div className="flex flex-wrap gap-3 px-5 py-1.5">
                <Link
                  to={`/olts/${olt.number ?? olt.id}`}
                  className="text-xs text-link hover:underline"
                >
                  Open {olt.name}
                </Link>
              </div>
            </Card>
          </MatchedPair>

          {/* What the subscriber is actually moving, right under the light that
              carries it — a link that is up and well-lit but idle, or pinned at
              its ceiling, is a different story the optics alone do not tell.

              What the line can actually carry, measured by the CPE itself over
              TR-143, sits BESIDE what it is carrying now on a wide screen (the
              operator ruled out the stack: two half-empty cards one above the
              other) and under it on a narrow one. Gated on its own capability:
              a diagnostic through the chassis door, held by the engineer and
              granted to the technician — and MatchedPair takes exactly two, so
              without the line test the traffic card keeps the whole width
              rather than pairing with a hole. */}
          {can('onu.speedtest') ? (
            <MatchedPair>
              <TrafficCard oltId={onu.oltId} onuId={onu.id} vendor={olt.vendor} />
              <LineTestCard onuId={onu.id} serial={onu.serial} onNotice={notify} />
            </MatchedPair>
          ) : (
            <TrafficCard oltId={onu.oltId} onuId={onu.id} vendor={olt.vendor} />
          )}

          {/* Where it sits, then what it is set up as. The summary card links
              out to the Configs tab to change any of what it shows — the detail
              editors no longer sit under the overview, they live in one place. */}
          <MatchedPair>
            <PlacementCard onu={onu} onNotice={notify} />
            <SettingsCard
              oltId={onu.oltId}
              onuId={onu.id}
              vendor={olt.vendor}
              deviceId={deviceId}
              onEdit={goToConfig}
            />
          </MatchedPair>

          {/* Who is actually behind the ONU. It belongs with what the ONU is
              rather than with the things that change it — an engineer asking
              "is anyone on this" is still reading, not editing.

              Shown for every ONU, not only those on an ACS: the OLT knows the
              MAC addresses it has learned, which is thinner than a host table
              and still the answer to "is anybody actually connected". */}
          <HostsCard
            device={device}
            macs={learnedMacs.data?.macs ?? []}
            mode={onu.managementMode ?? null}
            action={
              deviceId && onu.managementMode !== 'omci' ? (
                <DeviceScopeRefresh
                  deviceId={deviceId}
                  scope="hosts"
                  label="connected hosts"
                  onNotice={notify}
                />
              ) : (
                <ChassisRefresh
                  oltId={onu.oltId}
                  onuId={onu.id}
                  label="the MAC addresses the OLT has learned"
                  onNotice={notify}
                />
              )
            }
          />
        </div>
      )}

      {/* VLANs and ports belong to the ONU, whichever way it is managed. They
          hang off the device when there is one, so a subscriber who later
          informs an ACS keeps the config they already had; otherwise off the
          ONU itself. Only TR-069 and its task history genuinely need an ACS. */}
      {activeTab !== 'overview' && (
        <>
          {activeTab === 'vlans' && <VlansTab base={configBase} oltId={onu.oltId} onuId={onu.id} />}
          {activeTab === 'ports' && (
            <PortsTab
              base={configBase}
              oltId={onu.oltId}
              onuId={onu.id}
              chassisVendor={olt?.vendor ?? null}
              onApplyWithReboot={() => startResync({ reboot: true })}
              // The link pills read the router's LAN tree as the ACS last read it; this re-reads it.
              refresh={
                deviceId ? (
                  <DeviceScopeRefresh
                    deviceId={deviceId}
                    scope="lan"
                    label="the LAN ports"
                    invalidate={[['onu-config', configBase, 'ports']]}
                    onNotice={notify}
                  />
                ) : undefined
              }
            />
          )}
          {activeTab === 'macs' && (
            <MacTableTab
              macs={learnedMacs.data?.macs ?? []}
              hosts={device?.hosts ?? []}
              fetchedAt={learnedMacs.data?.fetchedAt ?? null}
              cached={learnedMacs.data?.cached === true}
              unread={learnedMacs.data?.unread === true}
              loading={learnedMacs.isLoading}
              // Both sides in one press: the OLT's read, then the router's host table where there is a router.
              refresh={async () => {
                await learnedMacs.refetch();
                if (deviceId && onu.managementMode !== 'omci') {
                  await api.post(`/devices/${encodeURIComponent(deviceId)}/refresh`, { scope: 'hosts' });
                }
                return { message: 'Re-read the MAC table.' };
              }}
              onNotice={notify}
            />
          )}
          {activeTab === 'voip' && (
            <VoipTab
              oltId={onu.oltId}
              onuId={onu.id}
              serial={onu.serial}
              vendor={olt.vendor}
              deviceId={deviceId}
            />
          )}
          {activeTab === 'iptv' && (
            <IptvTab
              oltId={onu.oltId}
              oltHref={`/olts/${olt.number ?? olt.id}`}
              onuId={onu.id}
              serial={onu.serial}
              vendor={olt.vendor}
            />
          )}
          {activeTab === 'events' && (
            <EventsTab oltId={onu.oltId} onuId={onu.id} onuRowId={onu.id} />
          )}
          {/* The key the page was OPENED with, not the row id: the endpoint
              resolves the number, the ONU id and the linked device id alike,
              and passing what is already in the address bar keeps the tab
              working on a link built from any of the three. */}
          {activeTab === 'swaps' && <SwapsTab onuKey={id} serial={onu.serial} />}
          {activeTab === 'configs' && (
            <ConfigsTab
              oltId={onu.oltId}
              onuId={onu.id}
              vendor={olt.vendor}
              deviceId={deviceId}
              device={device}
              deviceLoading={deviceQuery.isLoading}
              focus={configFocus}
              serial={onu.serial}
              onEditWan={() => setWanOpen(true)}
              // What the WAN card's IPv6 radios save through — the same
              // hand-off the WAN mode dialog below is given.
              onuHref={onuPath(onu)}
              onuQueryKey={['onu', id]}
              onNotice={notify}
              routerOwnsWifi={wifiOwnedByRouter({ managementMode: onu.managementMode ?? null, deviceId })}
              managementMode={managementState === 'omci' ? 'omci' : 'tr069'}
            />
          )}
          {activeTab === 'security' && (
            <SecurityTab
              oltId={onu.oltId}
              onuId={onu.id}
              vendor={olt.vendor}
              deviceId={deviceId}
              serial={onu.serial}
              onNotice={notify}
            />
          )}
          {activeTab === 'olt-config' && (
            <OltRunningConfigView oltId={onu.oltId} onuId={onu.id} serial={onu.serial} />
          )}
          {activeTab === 'tasks' &&
            (deviceId ? (
              <TasksTab
                deviceId={deviceId}
                // A warning's Resync opens the same review the header's Resync does.
                {...(can('onu.provision') ? { onResync: () => setResyncOpen(true) } : {})}
              />
            ) : (
              <NeedsAcs tab="tasks" serial={onu.serial} />
            ))}
        </>
      )}



      {/* The same actions as a footer bar, on phones only.

          Icon over label, spread across the width and fixed to the bottom —
          the shape a phone already uses for its own navigation, so it needs no
          learning. Three fit comfortably; the rest live behind More rather than
          being squeezed to a width nobody can hit. Delete is never one of the
          three, so the most destructive action is always a deliberate second
          tap. */}
      <OnuActionBar actions={actions} />

      {renaming && (
        <RenameDialog
          oltId={onu.oltId}
          onuId={onu.id}
          currentName={onu.name}
          serial={onu.serial}
          href={onuPath(onu)}
          onClose={() => setRenaming(false)}
        />
      )}

      {/* The third of the writes that never reach the OLT, with the rename and
          the note: this one only records that SimpleACS should leave the ONU
          alone. */}
      {disabling && (
        <DisableDialog
          kind="onu"
          name={onu.serial}
          endpoint={disableEndpoint}
          href={onuPath(onu)}
          // The chassis half rides only where the OLT reports the line state.
          {...(chassisLine === null
            ? {}
            : {
                chassis: {
                  oltName: olt.name,
                  preview: () =>
                    api.post<CliActionResult>(adminPath, { enable: false, dryRun: true }),
                  run: () => api.post<CliActionResult>(adminPath, { enable: false, dryRun: false }),
                },
              })}
          onClose={() => setDisabling(false)}
        />
      )}

      {/* Beside the rename, and pointedly not the same kind of write: that one
          goes to the chassis because the name lives there; this one goes
          nowhere but here. */}
      {noting && (
        <NoteDialog
          onuId={onu.id}
          currentNote={onu.notes ?? null}
          serial={onu.serial}
          onClose={() => setNoting(false)}
        />
      )}

      {/*
        Both management dialogs take their "current" from the CHASSIS's answer
        (`managementState`, the same source as the header icon), never from
        whether a device row is linked. A linked device is the worst proxy on
        this fleet: an F670L on TR-069 announces a router serial the chassis
        never heard, so it informs UNLINKED — Joyce - 629 wore a TR-069 icon
        in the header while these dialogs called her OMCI off the missing
        link. `tr069_other` counts as "on TR-069": it is — against somebody
        else's ACS, which is exactly the state these dialogs exist to change.
      */}
      {resyncOpen && onu && (
        <ResyncConfirmDialog
          oltId={onu.oltId}
          onuId={onu.id}
          serial={onu.serial}
          applying={false}
          onApply={startResync}
          onClose={() => setResyncOpen(false)}
        />
      )}

      {wanOpen && (
        <WanModeDialog
          base={configBase}
          oltId={onu.oltId}
          onuId={onu.id}
          vendor={olt.vendor}
          routerLinked={Boolean(deviceId)}
          serial={onu.serial}
          onClose={() => setWanOpen(false)}
          href={onuPath(onu)}
          invalidate={[['onu', id]]}
          // The firewall as the CPE reports it, so the wizard's Firewall step
          // opens on the same state the Security card and the pill show.
          cpeFirewall={device?.firewall ?? null}
        />
      )}

      {presetOpen && (
        <ApplyDevicePresetDialog
          onuId={onu.id}
          onuSerial={onu.serial}
          // The recorded intent, not `deviceId`: which way the operator last
          // pointed this ONU is what decides which preset is offered first.
          managementMode={onu.managementMode ?? null}
          onClose={() => setPresetOpen(false)}
          href={onuPath(onu)}
          invalidate={[['onu', id]]}
        />
      )}

      {rebootPlan && onu && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
          <div className="absolute inset-0 bg-black/60" onClick={() => setRebootPlan(null)} />
          <div className="relative w-full max-w-lg rounded-xl border border-line bg-surface shadow-2xl">
            <header className="border-b border-line px-5 py-4">
              <h2 className="text-sm font-semibold text-ink">Reboot {onu.serial}</h2>
              <p className="mt-0.5 text-xs text-muted">
                Through the OLT, so it works whether or not this ONU is on TR-069.
              </p>
            </header>
            <div className="space-y-3 p-5">
              {rebootPlan.warnings.map((w) => (
                <div
                  key={w}
                  className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn"
                >
                  {w}
                </div>
              ))}
            </div>
            <footer className="flex items-center justify-end gap-2 border-t border-line px-5 py-4">
              <Button variant="ghost" onClick={() => setRebootPlan(null)}>
                Cancel
              </Button>
              {/* Closes and reboots on the chassis in the background, like the
                  other chassis actions — an OLT reboot is not instant and the
                  operator should not be pinned to a spinner for it. */}
              <Button
                variant="danger"
                onClick={() => {
                  setRebootPlan(null);
                  tasks.start<CliActionResult>({
                    label: `Reboot ${onu.serial}`,
                    href: `/onus/${encodeURIComponent(id)}`,
                    run: () =>
                      api.post<CliActionResult>(
                        `/olts/${encodeURIComponent(onu.oltId)}/onus/${encodeURIComponent(onu.id)}/reboot`,
                        { dryRun: false },
                      ),
                    done: () => `${onu.serial} is rebooting — it re-ranges in about a minute.`,
                    invalidate: [['onu', id]],
                  });
                }}
              >
                Reboot now
              </Button>
            </footer>
          </div>
        </div>
      )}

      {modeOpen && olt && onu && (
        <ManagementModeDialog
          oltId={onu.oltId}
          onuId={onu.id}
          serial={onu.serial}
          vendor={olt.vendor}
          model={olt.model}
          currentMode={managementState === 'omci' ? 'omci' : 'tr069'}
          onClose={() => setModeOpen(false)}
          href={onuPath(onu)}
          invalidate={[['onu', id], ['onu-config', configBase]]}
        />
      )}

      {oltAction && (
        <OnuOltActionDialog
          kind={oltAction}
          olt={olt}
          onu={onu}
          onClose={() => setOltAction(null)}
          href={`/onus/${encodeURIComponent(id)}`}
          invalidate={[
            ['onu', id],
            ['onus'],
          ]}
        />
      )}

      {swapOpen && (
        <SwapOnuDialog
          olt={olt}
          onu={onu}
          onClose={() => setSwapOpen(false)}
          href={`/onus/${encodeURIComponent(id)}`}
          invalidate={[['onu', id, 'swaps']]}
        />
      )}
    </>
  );
}

/**
 * Shown wherever a section needs the ACS and the ONU has never reached it. It
 * says which ONU and what would change that, because "no data" on its own reads
 * like a fault when the real answer is that nothing is wrong.
 */
/**
 * Why one tab in particular has nothing to show for an OMCI ONU.
 *
 * The same sentence on every tab tells an operator only that something is
 * missing. Which of these need an ACS and which merely have not been wired to
 * the chassis yet are different answers, and only one of them is a thing they
 * can fix by switching the ONU over.
 */
/**
 * A card nobody opened costs a click; a card everybody scrolls past costs the
 * page. These are the editing surfaces — WAN, LAN, WiFi, hosts — and an
 * operator reaches one at a time, having already read the summary above.
 */
/**
 * The per-card refresh, CPE side: one scope, one connection request, only the
 * objects that card is drawn from (`POST /devices/:id/refresh`).
 */
function DeviceScopeRefresh({
  deviceId,
  scope,
  label,
  invalidate = [],
  onNotice,
}: {
  deviceId: string;
  /**
   * One scope, or several read in turn — the ACS details panel re-reads the
   * management server AND the device objects (uptime, last inform, addresses)
   * it shows on top, the `device` read the removed Device panel used to own.
   */
  scope: DeviceRefreshScope | DeviceRefreshScope[];
  label: string;
  invalidate?: QueryKey[];
  onNotice: (message: string, tone?: 'info' | 'danger') => void;
}) {
  const scopes = Array.isArray(scope) ? scope : [scope];
  return (
    <RefreshButton
      label={label}
      run={async () => {
        let last: unknown = null;
        for (const one of scopes) {
          last = await api.post(`/devices/${encodeURIComponent(deviceId)}/refresh`, { scope: one });
        }
        return last;
      }}
      invalidate={[['device', deviceId], ...invalidate]}
      onNotice={onNotice}
    />
  );
}

/**
 * The per-card refresh, chassis side: `optics` re-reads state and light
 * (`refresh-optics`), otherwise the ONU's own configuration over OMCI
 * (`omci-config?refresh=1`) — the two reads Summon makes of the OLT.
 */
function ChassisRefresh({
  oltId,
  onuId,
  label,
  optics = false,
  invalidate = [],
  onNotice,
}: {
  oltId: string;
  onuId: string;
  label: string;
  optics?: boolean;
  invalidate?: QueryKey[];
  onNotice: (message: string, tone?: 'info' | 'danger') => void;
}) {
  const base = `/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(onuId)}`;
  return (
    <RefreshButton
      label={label}
      run={() =>
        optics
          ? api.post(`${base}/refresh-optics`, {}).then(() => ({ message: `Re-read ${label} from the OLT.` }))
          : api.get(`${base}/omci-config?refresh=1`).then(() => ({ message: `Re-read ${label} from the OLT.` }))
      }
      invalidate={[['onu', onuId], ['onu', onuId, 'omci-config'], ...invalidate]}
      onNotice={onNotice}
    />
  );
}

function Collapsible({
  id,
  title,
  children,
  embedded,
  defaultOpen = false,
  action,
}: {
  /**
   * Its key in the enclosing `CollapsibleGroup`: stacked panels open one at
   * a time (operator, 2026-09-13), so a panel with an id inside a group opens
   * and closes through the group — clicking an open one closes it, clicking
   * another closes the rest. Without an id, or outside a group, the panel
   * keeps its own state as it always did.
   */
  id?: string;
  title: React.ReactNode;
  children: React.ReactNode;
  /** Sits beside the chevron, outside the toggle — a refresh button, usually. */
  action?: React.ReactNode;
  /**
   * The child renders header-less (`embedded`), so this bar is the panel's ONE
   * and only header — drawn in the card-header style, with the title and its
   * status pill, so a collapsed panel reads exactly like the card it opens.
   * Without it (a section wrapping content that keeps its own headers), the bar
   * stays a slim toggle and its title is hidden while open so it does not double
   * the child's header.
   */
  embedded?: boolean;
  /**
   * Open on mount — the Configs tab opens the accordion the overview linked
   * to. In a group it is a claim on the group's one open key, first claim
   * wins, so the linked panel opens and the others start shut.
   */
  defaultOpen?: boolean;
}) {
  const { open, toggle } = useCollapsibleItem(id, defaultOpen);
  // The forms beneath report an edit in progress (useUnsavedDraft); a shut
  // panel holding one keeps its body mounted — hidden — and wears a pill, so
  // the group closing it for a sibling never loses the draft.
  const { dirty, report } = useDraftHolder();
  const mounted = panelBodyMounted(open, dirty);
  const unsaved = dirty && !open && (
    <Pill tone="warn">unsaved edits</Pill>
  );
  return (
    <div className="overflow-hidden rounded-xl border border-line bg-surface">
      <div
        className={`flex items-stretch ${embedded && open ? 'border-b border-line' : ''}`}
        style={
          embedded
            ? { background: 'color-mix(in srgb, var(--section-accent, #3b82f6) 12%, transparent)' }
            : undefined
        }
      >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className={
          embedded
            ? 'flex min-w-0 flex-1 items-center justify-between gap-3 px-5 py-3.5 text-left transition hover:brightness-95'
            : `flex min-w-0 flex-1 items-center justify-between gap-3 px-5 text-left hover:bg-surface-2 ${
                open ? 'py-1.5' : 'py-3'
              }`
        }
      >
        {embedded ? (
          <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
            {title}
            {unsaved}
          </h2>
        ) : (
          // A section over content that keeps its own header: hide the title
          // while open so the two do not stack; keep it as the collapsed label.
          <span className="flex items-center gap-2">
            <span className={`text-sm font-medium text-ink ${open ? 'sr-only' : ''}`}>{title}</span>
            {unsaved}
          </span>
        )}
        <ChevronDown
          className={`size-4 shrink-0 text-muted transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {action && <div className="flex items-center pr-3">{action}</div>}
      </div>
      {/* Mounted only when open: these cards each run their own queries, and
          five of them loading behind a closed panel is five requests nobody
          asked for. The one exception is a body holding an unapplied edit
          (panelBodyMounted) — it stays mounted, hidden, until the operator
          applies or discards it, because unmounting IS discarding. */}
      {mounted && (
        <div hidden={!open} className={embedded ? '' : 'border-t border-line'}>
          <DraftScope report={report}>{children}</DraftScope>
        </div>
      )}
    </div>
  );
}

/**
 * Every config the platform can write to one ONU, in one place, mode-aware.
 *
 * The prefer-ONU-config rule decides what shows. On TR-069 the CPE answers for
 * its own WAN, LAN, WiFi and security, so those editors are the CPE's — the
 * accordions that used to sit under the overview, plus the raw parameter tree
 * for a power user. On OMCI the ONU has never informed an ACS, so the editors
 * are the ones the OLT can carry over OMCI (ZTE) or the service the ONT reports
 * (Huawei). Security sits at the top either way, because a simple firewall
 * on/off is what an operator reaches for most, and the `/firewall` route already
 * decides between the CPE and the OLT verb underneath it.
 */
/**
 * Set WiFi on an ONU the ACS has not heard from yet.
 *
 * With no TR-069 device on this ACS there is nothing to write to live, so the
 * editors above are blank. This stores the SSID and passphrase as *desired* and
 * SimpleACS pushes them over TR-069 the moment the ONU informs — the "queue it
 * now, apply on inform" half of the WiFi feature. The pending/applied banners at
 * the top of the page report the rest; this card is only the way in.
 *
 * The passphrase is sent once to be stored (encrypted, never read back or
 * logged), so the field starts blank and clears after a save — this card sets
 * WiFi, it does not display it.
 */
function WifiAheadCard({
  oltId,
  onuId,
  onNotice,
}: {
  oltId: string;
  onuId: string;
  onNotice: (message: string, tone?: 'info' | 'danger') => void;
}) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const [ssid, setSsid] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [reveal, setReveal] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      api.post<{ ok: true; deferred: boolean }>(`/olts/${oltId}/onus/${onuId}/wifi/desired`, {
        ssid: ssid.trim(),
        passphrase: passphrase.trim() || undefined,
      }),
    onSuccess: async (r) => {
      onNotice(
        r.deferred
          ? 'WiFi queued — it applies the moment the ONU next informs the ACS.'
          : 'WiFi pushed to the ONU.',
      );
      setPassphrase('');
      // The banner at the top of the page reads onu.wifiPending; refetch so it
      // shows without waiting on the 15s live refresh.
      await queryClient.invalidateQueries({ queryKey: ['onu'] });
    },
    onError: (err) =>
      onNotice(err instanceof Error ? err.message : 'Could not queue the WiFi.', 'danger'),
  });

  // Gated on the same capability the endpoint requires (onu.provision), so the
  // card is never shown to someone who would only be refused on submit.
  if (!can('onu.provision')) return null;

  const tooShort = passphrase.trim().length > 0 && passphrase.trim().length < 8;
  const disabled = !ssid.trim() || tooShort || save.isPending;

  return (
    <Collapsible
      id="wifi-ahead"
      embedded
      title={
        <span className="flex items-center gap-2">
          <Wifi className="size-4 text-muted" />
          Set WiFi ahead of time
        </span>
      }
    >
      <div className="space-y-4 p-5">
        <p className="text-sm text-muted">
          This ONU is not on the ACS yet, so its WiFi cannot be edited live. Set the network name
          and passphrase here — SimpleACS applies them over TR-069 the moment the ONU informs, and
          the banner at the top of this page tracks it from queued to applied.
        </p>
        <Field label="Network name (SSID)">
          <Input
            value={ssid}
            onChange={(e) => setSsid(e.target.value)}
            placeholder="e.g. Gachie-Nebula"
            maxLength={32}
          />
        </Field>
        <Field
          label="Passphrase"
          hint={
            tooShort
              ? 'At least 8 characters.'
              : 'Leave blank to change only the network name and keep the current passphrase.'
          }
        >
          <div className="flex gap-2">
            <Input
              type={reveal ? 'text' : 'password'}
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              placeholder="••••••••"
              autoComplete="off"
            />
            <Button variant="ghost" type="button" onClick={() => setReveal((v) => !v)}>
              {reveal ? 'Hide' : 'Show'}
            </Button>
          </div>
        </Field>
        <div className="flex justify-end">
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={disabled}
            onClick={() => save.mutate()}
          >
            Queue WiFi
          </Button>
        </div>
      </div>
    </Collapsible>
  );
}

function ConfigsTab({
  oltId,
  onuId,
  vendor,
  deviceId,
  device,
  deviceLoading,
  focus,
  serial,
  onEditWan,
  onuHref,
  onuQueryKey,
  onNotice,
  routerOwnsWifi,
  managementMode,
}: {
  oltId: string;
  onuId: string;
  vendor: string;
  deviceId: string | null;
  device: DeviceDetail | null;
  deviceLoading: boolean;
  focus: ConfigFocus | null;
  serial: string;
  /** Opens the page's WAN mode dialog — the WAN card's Edit goes there for the VLAN, mode and firewall. */
  onEditWan: () => void;
  /** The ONU's own page, where the WAN card's IPv6 tray row links. */
  onuHref: string;
  /** The page's ONU query, refreshed once the WAN card's IPv6 write lands. */
  onuQueryKey: QueryKey;
  onNotice: (message: string, tone?: 'info' | 'danger') => void;
  /** Managed over TR-069: the OLT's SSID card reads, it does not edit. */
  routerOwnsWifi: boolean;
  /** How the ONU is managed now — the management card's addressing change keeps it. */
  managementMode: 'tr069' | 'omci';
}) {
  const { can } = useSession();
  // The one-shot wake of a never-read CPE, once per visit to this tab — it
  // was the Device panel's, and that panel is gone (see useSummonUnreadOnce).
  useSummonUnreadOnce(device, onNotice);
  // On TR-069 in fact only when this ACS has the device — an ONU informing some
  // other ACS is not one we can drive over TR-069, so it gets the OMCI editors.
  // Security (firewall, access, accounts) has its own tab; port-forwarding lives
  // here, beside the WAN it exposes.
  //
  // The ORDER is the operator's (2026-09-13: "WAN, LAN, WIFI, WIFI Advanced,
  // Port Forwarding, TIME, VoIP, Diagnostics, Management Server (rename to ACS
  // Details), remove device"), then the panels they did not name in their old
  // relative order — Network services, the raw tree. "WAN over OMCI" stays
  // directly under WAN: it is the chassis's view of the same thing. Every
  // panel is in one CollapsibleGroup, so one is open at a time.
  const portForward = device?.portForward;
  /**
   * Fall back to the chassis when the ACS has no useful WAN. A freshly
   * authorised ONU informs the ACS before it has walked its WAN, so the
   * TR-069 card is blank — or classifies as "unknown" with no address — while
   * the "ONU as deployed" card already shows a WAN off the live chassis read
   * (an IP and VLAN). That mismatch is what the operator hit. So whenever the
   * ACS view carries nothing to act on — no WAN connection at all, or one it
   * cannot classify and has no address for — show what the chassis actually
   * carries over OMCI (the SAME omci-config read the deployed card uses).
   */
  const chassisWanShown =
    device !== null &&
    (device.wan.candidates.length === 0 || (device.wan.mode === 'unknown' && !device.wan.externalIp));
  /**
   * ONE panel starts open, decided here (configsInitialOpenKey): the deep
   * link, else the chassis WAN fallback when shown, else none. Deciding it
   * per panel — the fallback with a bare `defaultOpen` — let the fallback,
   * which sits above LAN and WiFi, take the group's first claim and shut the
   * editor the overview had linked to on every unit whose ACS WAN was empty
   * (review, 2026-09-13).
   */
  const initialOpen = configsInitialOpenKey(focus, chassisWanShown);
  // The WAN card's IPv6 rows read GET …/wan-ipv6 under this scope (the WAN
  // mode dialog's key), so the WAN refresh and the page loop re-read them too.
  const ipv6Scope = deviceId ? wanIpv6QueryScope(`/devices/${encodeURIComponent(deviceId)}`) : null;
  /**
   * The platform's recorded firewall (`onus.firewalled`), for where the WAN
   * card's Firewall row opens when the CPE reports none of its own — the
   * SAME omci-config read the deployed card, the Security tab and the WAN
   * mode dialog share (one key, served from the stored snapshot; never a
   * CLI session from here), so it costs nothing and the three can never
   * open on different states.
   */
  const omciConfig = useQuery({
    queryKey: ['onu', onuId, 'omci-config'],
    queryFn: () =>
      api.get<{ firewalled?: boolean | null }>(
        `/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(onuId)}/omci-config`,
      ),
    enabled: Boolean(deviceId),
  });
  return (
    <div className="space-y-4">
      {deviceId ? (
        device ? (
          <CollapsibleGroup>
            <Collapsible
              id="wan"
              embedded
              title={wanCardTitle(device, omciConfig.data?.firewalled ?? null)}
              defaultOpen={initialOpen === 'wan'}
              action={<DeviceScopeRefresh deviceId={deviceId} scope="wan" label="the WAN" invalidate={ipv6Scope ? [ipv6Scope] : []} onNotice={onNotice} />}
            >
              <WanCard
                device={device}
                onNotice={onNotice}
                embedded
                onEditIpv6={onEditWan}
                // The IPv6 radios and the Firewall row save through the
                // wizard's own sequence, so the card is given exactly what
                // the wizard is: the ONU, its chassis, where the tray row
                // links, what to refresh, and the recorded firewall.
                ipv6Apply={{
                  oltId,
                  onuId,
                  vendor,
                  serial,
                  href: onuHref,
                  invalidate: [onuQueryKey],
                  firewalled: omciConfig.data?.firewalled ?? null,
                }}
              />
            </Collapsible>
            {/* Every OTHER connection to the outside world, one card each —
                the hotspot bridge, IPTV, the TR-069 channel (operator,
                2026-10-05: "split the wans into different cards, not share
                one"). The subscriber WAN above keeps the editor. */}
            {otherWanConnections(device.wan.candidates).map((c) => (
              <Collapsible
                key={c.path}
                id={`wan-${c.path}`}
                embedded
                title={wanConnectionTitle(c, firewallTitlePill(device.firewall, omciConfig.data?.firewalled ?? null))}
              >
                {c.role === 'management' ? (
                  // The internet card's controls, for the platform's own channel (operator, 2026-10-10).
                  <ManagementWanCard
                    device={device}
                    connection={c}
                    oltId={oltId}
                    onuId={onuId}
                    serial={serial}
                    mode={managementMode}
                    firewalled={omciConfig.data?.firewalled ?? null}
                    href={onuHref}
                    invalidate={[['device', deviceId], ['onu', onuId], ['onu', onuId, 'omci-config']]}
                  />
                ) : (
                <WanConnectionCard
                  connection={c}
                  ports={
                    <WanPorts
                      connection={c}
                      base={`/devices/${encodeURIComponent(deviceId)}`}
                      serial={serial}
                      editable={can('onu.provision')}
                      href={onuHref}
                    />
                  }
                  deleteAction={
                    wanDeletable(c) && can('onu.provision') ? (
                      <WanDeleteButton
                        connection={c}
                        deviceId={deviceId}
                        endpoint={`/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(onuId)}/wan`}
                        serial={serial}
                        href={onuHref}
                        invalidate={[['device', deviceId], ['onu', onuId], ['onu-config']]}
                      />
                    ) : undefined
                  }
                />
                )}
              </Collapsible>
            ))}
            {chassisWanShown && (
              <Collapsible
                id="wan-omci"
                embedded
                title="WAN over OMCI · from the chassis"
                defaultOpen={initialOpen === 'wan-omci'}
                action={<ChassisRefresh oltId={oltId} onuId={onuId} label="the WAN the OLT carries" onNotice={onNotice} />}
              >
                <p className="px-4 pt-3 text-xs text-muted">
                  The router reports no subscriber WAN of its own — not walked yet on a new unit, or
                  none on a bridged one. Below is what the OLT carries for it over OMCI.
                </p>
                <OmciConfigCards oltId={oltId} onuId={onuId} routerOwnsWifi={routerOwnsWifi} />
              </Collapsible>
            )}
            <Collapsible
              id="lan"
              embedded
              title={lanCardTitle(device)}
              defaultOpen={initialOpen === 'lan'}
              action={<DeviceScopeRefresh deviceId={deviceId} scope="lan" label="the LAN" onNotice={onNotice} />}
            >
              <LanCard device={device} onNotice={onNotice} embedded />
            </Collapsible>
            <Collapsible
              id="wifi"
              embedded
              title={wifiCardTitle(device)}
              defaultOpen={initialOpen === 'wifi'}
              action={<DeviceScopeRefresh deviceId={deviceId} scope="wifi" label="the WiFi" onNotice={onNotice} />}
            >
              <WifiCard device={device} onNotice={onNotice} embedded />
            </Collapsible>
            {/* Model-aware sections — each resolves against the CPE and shows only
                what the model exposes, so they are safe to offer on every TR-069
                ONU. They fetch only when opened. */}
            <Collapsible
              id="wifi-advanced"
              embedded
              title="WiFi advanced"
              action={<DeviceScopeRefresh deviceId={deviceId} scope="wifi_advanced" label="the radio settings" invalidate={[['device', deviceId, 'config', 'wifi_advanced']]} onNotice={onNotice} />}
            >
              <ConfigSectionCard deviceId={deviceId} section="wifi_advanced" onNotice={onNotice} />
            </Collapsible>
            {/* Port-forwarding is a WAN-side plumbing choice, not a Security
                matter — a panel of its own, offered only where the model reports
                a PortMapping table. */}
            {portForward?.supported && (
              <Collapsible id="port-forwarding" embedded title="Port forwarding">
                <PortForwards
                  deviceId={deviceId}
                  entries={portForward.entries}
                  canWrite={can('device.write')}
                  remoteHostSupported={portForward.remoteHostSupported}
                  onNotice={onNotice}
                />
              </Collapsible>
            )}
            <Collapsible
              id="time"
              embedded
              title="Time & NTP"
              action={<DeviceScopeRefresh deviceId={deviceId} scope="time" label="the clock and NTP" invalidate={[['device', deviceId, 'config', 'time']]} onNotice={onNotice} />}
            >
              <ConfigSectionCard deviceId={deviceId} section="time" onNotice={onNotice} />
            </Collapsible>
            <Collapsible id="diagnostics" embedded title="Diagnostics">
              <DiagnosticsCard deviceId={deviceId} onNotice={onNotice} />
            </Collapsible>
            {/* "ACS details" — the management-server section (its key stays
                `management`), with what the ACS knows about the CPE on top: the
                old Device panel's read rows (last inform, uptime, management IP,
                data model, GenieACS id) and, under the controls, its record
                editor. Serial/manufacturer/model/firmware are on the overview. */}
            <Collapsible
              id="management"
              embedded
              title="ACS details"
              action={<DeviceScopeRefresh deviceId={deviceId} scope={['management', 'device']} label="the ACS details" invalidate={[['device', deviceId, 'config', 'management']]} onNotice={onNotice} />}
            >
              <AcsDeviceFacts device={device} />
              <ConfigSectionCard deviceId={deviceId} section="management" onNotice={onNotice} />
              <DeviceRecordEditor device={device} onNotice={onNotice} />
            </Collapsible>
            <Collapsible
              id="network"
              embedded
              title="Network services"
              action={<DeviceScopeRefresh deviceId={deviceId} scope="network" label="the network services" invalidate={[['device', deviceId, 'config', 'network'], ['device', deviceId, 'port-forwards']]} onNotice={onNotice} />}
            >
              <ConfigSectionCard deviceId={deviceId} section="network" onNotice={onNotice} />
            </Collapsible>
            {/* Everything else the router allows, off its own tree (operator,
                2026-10-05: "show us and allow us to configure whatever the onu
                allows") — labelled, editable where the router says so, with
                what the platform holds back shown and explained. Its
                "Show read-only too" is the old raw parameter list. */}
            <Collapsible id="all-settings" embedded title="All settings">
              <AllSettings deviceId={deviceId} onNotice={onNotice} />
            </Collapsible>
          </CollapsibleGroup>
        ) : deviceLoading ? (
          <Card>
            <div className="flex justify-center p-8">
              <Spinner />
            </div>
          </Card>
        ) : null
      ) : (
        <CollapsibleGroup>
          {vendor === 'huawei' && (
            <Collapsible id="service-config" title="Service configuration">
              <OntServiceConfigCard oltId={oltId} onuId={onuId} />
            </Collapsible>
          )}
          {/* What the OLT can read and write over OMCI — the WAN and, on ZTE,
              the wifi and SSIDs. This is the OMCI counterpart of the CPE editors
              above, shown because the ONU is not on TR-069. */}
          <OmciConfigCards oltId={oltId} onuId={onuId} routerOwnsWifi={routerOwnsWifi} />
          {/* Set WiFi before the ONU is on the ACS: stored now, pushed over
              TR-069 on its next inform. The one editor here that writes forward
              rather than reading the chassis, which is why it sits below the
              OMCI cards and above the "not on the ACS" note it answers. */}
          <WifiAheadCard oltId={oltId} onuId={onuId} onNotice={onNotice} />
          <NotOnAcs serial={serial} />
        </CollapsibleGroup>
      )}
    </div>
  );
}

/**
 * The ONU's security surface, mode-aware, in accordions like the Configs tab.
 *
 * On TR-069 the CPE answers for what it exposes — `GET /devices/:id/security`
 * returns only the controls the model actually has (Huawei's per-service WAN/LAN
 * access matrix, firewall level, WAN ICMP, accounts; ZTE's narrower set) — so
 * every accordion here is real and nothing unsupported is offered. (Port-forwarding
 * moved to the Configs tab, beside the WAN/DMZ exposure it belongs with.) On OMCI
 * (no CPE on this ACS) all that remains is the firewall over the OLT verb, plus a
 * note that the rest is a TR-069 feature.
 */
function SecurityTab({
  oltId,
  onuId,
  vendor,
  deviceId,
  serial,
  onNotice,
}: {
  oltId: string;
  onuId: string;
  vendor: string;
  deviceId: string | null;
  serial: string;
  onNotice: (message: string, tone?: 'info' | 'danger') => void;
}) {
  const securityQuery = useQuery({
    queryKey: ['device', deviceId, 'security'],
    queryFn: () => api.get<SecurityState>(`/devices/${encodeURIComponent(deviceId!)}/security`),
    enabled: Boolean(deviceId),
  });
  // The OMCI-side recorded firewall, for an ONU with no CPE to ask.
  const omci = useQuery({
    queryKey: ['onu', onuId, 'omci-config'],
    queryFn: () =>
      api.get<{ firewalled?: boolean | null }>(
        `/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(onuId)}/omci-config`,
      ),
    enabled: !deviceId,
  });

  if (!deviceId) {
    return (
      <div className="space-y-4">
        <Card
          title={
            <span className="flex items-center gap-2">
              <ShieldCheck className="size-4 text-muted" />
              Security
            </span>
          }
          action={
            deviceId ? (
              <DeviceScopeRefresh
                deviceId={deviceId}
                scope="security"
                label="the security settings"
                invalidate={[['device', deviceId, 'security']]}
                onNotice={onNotice}
              />
            ) : (
              <ChassisRefresh oltId={oltId} onuId={onuId} label="the firewall flag" onNotice={onNotice} />
            )
          }
        >
          <OmciFirewall
            oltId={oltId}
            onuId={onuId}
            vendor={vendor}
            firewalled={omci.data?.firewalled ?? null}
            onNotice={onNotice}
          />
        </Card>
        <NotOnAcs serial={serial} />
      </div>
    );
  }

  if (securityQuery.isLoading) {
    return (
      <Card>
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      </Card>
    );
  }

  const groups = securityQuery.data?.groups ?? [];

  return (
    <div className="space-y-4">
      {/* One group open at a time, the first to begin with — the same rule as
          the Configs stack. */}
      <CollapsibleGroup>
        {groups.map((group, i) => (
          <Collapsible key={group.key} id={group.key} embedded title={group.label} defaultOpen={i === 0}>
            <SecurityGroupControls group={group} deviceId={deviceId} onNotice={onNotice} />
          </Collapsible>
        ))}
      </CollapsibleGroup>

      {groups.length === 0 && (
        <Card>
          <p className="p-5 text-sm text-muted">
            This ONU reports no security controls over TR-069 yet — summon it, or its model exposes
            none.
          </p>
        </Card>
      )}
    </div>
  );
}

function NeedsAcs({ tab, serial }: { tab: Tab; serial: string }) {
  const reason: Record<string, { title: string; description: string }> = {
    // VLANs and ports no longer belong here: both are written to the chassis
    // over the CLI and work whether or not an ACS has ever heard of the ONU.
    tasks: {
      title: 'Nothing queues without an ACS',
      description: `Tasks are the ACS's own queue of work for a device — reboots, parameter writes, firmware. ${serial} has never informed one, so there is no queue. The OLT-side actions above work regardless.`,
    },
  };
  const it = reason[tab] ?? {
    title: 'This ONU is managed over OMCI',
    description: `${serial} has never informed this ACS.`,
  };
  return (
    <Card title={it.title}>
      <EmptyState icon={<CloudOff className="size-8" />} title={it.title} description={it.description} />
    </Card>
  );
}

function NotOnAcs({ serial }: { serial: string }) {
  return (
    <Card title="Not on SimpleACS TR-069">
      <EmptyState
        icon={<CloudOff className="size-8" />}
        title="This ONU is managed over OMCI"
        description={`${serial} has never informed this ACS. Its WAN and wifi are read from the OLT above; what TR-069 adds on top is the connected-host list, per-radio client detail and the task queue. Point the ONU's TR-069 client at this ACS to fill those in.`}
      />
    </Card>
  );
}

/**
 * Ninety days of daily light readings.
 *
 * The line is the day's WORST receive level, not its average: a splice that
 * fails for an hour every afternoon averages away to nothing, and the worst
 * reading is what a subscriber actually experienced, and the bar's height is
 * how much the light moved that day — so a wandering reading looks different
 * from a steady one that has simply dropped.
 *
 * Drawn as an inline SVG rather than a chart library — ninety bars do not
 * justify the dependency.
 */
/**
 * Two cards side by side at one height — the row's — with no scrollbars.
 *
 * The first cut capped both at the shorter card's height and let the taller
 * scroll inside itself; the operator ruled the scrollbars out. So the pair
 * stretches instead: both borders run to the same line, and the real work of
 * closing the gap is done by the cards carrying less whitespace, not by
 * hiding what would not fit. Stacked on a narrow screen, each card keeps its
 * own height as before.
 */
function MatchedPair({ children }: { children: [ReactNode, ReactNode] }) {
  return (
    <div className="grid items-stretch gap-5 xl:grid-cols-2">
      <div className="min-w-0 [&>*]:h-full">{children[0]}</div>
      <div className="min-w-0 [&>*]:h-full">{children[1]}</div>
    </div>
  );
}

function OpticalTrend({
  history,
  loading,
  embedded,
}: {
  history: OpticalHistory | undefined;
  loading: boolean;
  /** Rendered inside the General card, so it supplies no card of its own. */
  embedded?: boolean;
}) {
  const points = (history?.items ?? []).filter((d) => d.rxMin !== null);
  const verdict = history?.verdict;

  /**
   * The chart draws in real pixels, not a scaled viewBox. Scaling a fixed
   * 720-unit drawing into whatever cell the card offers shrank the axis text
   * to specks — the labels were there and unreadable, which to the person
   * looking at it is the same as not there. Measured width, fixed height,
   * 10px text that is actually 10px.
   */
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [W, setW] = useState(560);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setW(Math.max(320, Math.floor(el.clientWidth)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
    // Re-attach when the chart replaces the loading or empty state — the div
    // this measures does not exist until then, and an effect that ran against
    // null would otherwise leave the default width for ever.
  }, [loading, points.length]);

  const tone =
    verdict?.status === 'critical'
      ? 'danger'
      : verdict?.status === 'degrading'
        ? 'warn'
        : verdict?.status === 'ok'
          ? 'success'
          : 'neutral';

  const title = (
    <span className="flex items-center gap-2">
      <Activity className="size-4 text-muted" />
      Light over time
      {verdict && verdict.status !== 'insufficient-data' && (
        <Pill state tone={tone}>{verdict.status}</Pill>
      )}
    </span>
  );

  // A card of its own when it stands alone; a titled section when it sits
  // inside the General card, since a card nested in a card reads as a mistake.
  const Frame = ({ children }: { children: React.ReactNode }) =>
    embedded ? (
      // A cell in the General card's bottom row, so no border of its own — the
      // grid draws the dividers. A column, so the caption can sit at the
      // bottom of whatever height the row has.
      <div className="flex h-full min-w-0 flex-col">
        <div
          className="px-5 pt-2 text-xs uppercase tracking-wide text-faint"
          title={`The line follows each day's reading across ${LIGHT_WINDOW_DAYS} days.`}
        >
          {title}
        </div>
        {children}
      </div>
    ) : (
      <Card title={title}>{children}</Card>
    );

  if (loading) {
    return (
      <Frame>
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      </Frame>
    );
  }

  if (points.length < 2) {
    return (
      <Frame>
        <EmptyState
          title="Not enough readings yet"
          description="A point is recorded each day a sync reads this ONU's light. A trend needs a few days before it means anything."
        />
      </Frame>
    );
  }

  // 185 → 120 with the 2026-09-10 "30% shorter" ask; the axes scale with it.
  const H = 95;
  // A real pair of axes, so this reads as dB against time rather than a shape
  // floating in a box (the operator asked twice). Left pad carries the dBm
  // labels, bottom the dates.
  const L = 40;
  const R = 12;
  const T = 12;
  const B = 26;
  const lows = points.map((p) => p.rxMin!);
  const highs = points.map((p) => p.rxMax ?? p.rxMin!);
  let min = Math.min(...lows) - 1;
  let max = Math.max(...highs) + 1;
  // At least a 6 dB window. Stretched to fit, a healthy link's half-decibel
  // wobble fills the whole chart and reads as a cliff; against a fixed-ish
  // scale, flat looks flat and degradation looks like the slope it is.
  if (max - min < 6) {
    const mid = (max + min) / 2;
    min = mid - 3;
    max = mid + 3;
  }
  const span = max - min || 1;
  const y = (dbm: number) => T + ((max - dbm) / span) * (H - T - B);

  /**
   * Y ticks on whole decibels at a step that yields 3–6 lines. Gridlines are
   * hairline and recessive: the data wears the color, the scaffolding does
   * not.
   */
  const step = span <= 4 ? 1 : span <= 12 ? 2 : span <= 30 ? 5 : 10;
  const yTicks: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max; v += step) yTicks.push(v);

  /**
   * A line through each day's worst reading, over a band spanning the day's
   * range. The worst is the honest series: it is the number the degradation
   * verdict is computed from, and light that is fine at noon and poor at
   * midnight is a poor link. The band keeps how much each day wandered.
   *
   * X is the actual date, not the reading's index. Only days a sync ran have
   * a point, so index spacing would show a fortnight's outage as one day wide
   * — a gap in the readings should look like a gap in time.
   */
  const t0 = new Date(points[0]!.day).getTime();
  const t1 = new Date(points[points.length - 1]!.day).getTime();
  const tSpan = Math.max(t1 - t0, 1);
  const x = (i: number) =>
    L + ((new Date(points[i]!.day).getTime() - t0) / tSpan) * (W - L - R);

  /**
   * A date under EVERY point (operator's call — no skipped days). Each label
   * needs about 40px of "6 Aug"; when the readings outgrow the width, every
   * Nth is labelled so neighbours never overlap — thinning starts only when
   * physics forces it, and every point always keeps its tick mark and its
   * hover date either way.
   */
  const DAY_MS = 86_400_000;
  const LABEL_W = 40;
  const labelEvery = Math.max(1, Math.ceil((points.length * LABEL_W) / (W - L - R)));
  const labelled = points
    .map((_, i) => i)
    .filter((i) => i % labelEvery === 0 || i === points.length - 1)
    // The forced last label wins a collision with its neighbour.
    .filter((i, _, arr) => i === points.length - 1 || x(points.length - 1) - x(i) >= LABEL_W || !arr.includes(points.length - 1));
  const dateLabel = (t: number) =>
    formatDayMonth(t);

  const stroke =
    tone === 'danger' ? 'var(--danger)' : tone === 'warn' ? 'var(--warn)' : 'var(--success)';

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.rxMin!).toFixed(1)}`)
    .join(' ');
  // Band: along the daily bests left to right, back along the worsts.
  const bandPath =
    points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.rxMax ?? p.rxMin!).toFixed(1)}`).join(' ') +
    ' ' +
    [...points]
      .reverse()
      .map((p, i) => `L ${x(points.length - 1 - i).toFixed(1)} ${y(p.rxMin!).toFixed(1)}`)
      .join(' ') +
    ' Z';

  return (
    <Frame>
      <div className="px-4 py-1.5">
        <div ref={wrapRef} className="w-full">
          <svg width={W} height={H} className="block" role="img"
            aria-label={`Receive level in dBm against time over the last ${points.length} readings`}>
          {/* The dB scale: hairline gridlines with the value at the left. */}
          {yTicks.map((v) => (
            <g key={v}>
              <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="var(--line)" strokeWidth={1} />
              {/* Series-coloured, not muted ink — the operator called the
                  grey unreadable on the dark ground, and the green is the one
                  colour this chart already guarantees legible. */}
              <text
                x={L - 6}
                y={y(v) + 3}
                textAnchor="end"
                fontSize="10"
                fill={stroke}
                fillOpacity={0.85}
                className="tabular-nums"
              >
                {v}
              </text>
            </g>
          ))}
          <text x={12} y={T + 8} fontSize="9" fill={stroke} fillOpacity={0.7} transform={`rotate(-90 12 ${T + 8})`} textAnchor="end">
            dBm
          </text>
          {/* The -25 dBm line: below it a GPON receiver stops being dependable. */}
          {min < -25 && max > -25 && (
            <g>
              <line
                x1={L}
                x2={W - R}
                y1={y(-25)}
                y2={y(-25)}
                stroke="var(--warn)"
                strokeDasharray="4 4"
                strokeOpacity={0.6}
              />
              <text x={W - R} y={y(-25) - 4} textAnchor="end" fontSize="10" fill="var(--warn)">
                -25 dBm
              </text>
            </g>
          )}
          <path d={bandPath} fill={stroke} fillOpacity={0.12} />
          <path
            d={linePath}
            fill="none"
            stroke={stroke}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {points.map((p, i) => {
            const worst = p.rxMin!;
            const best = p.rxMax ?? worst;
            const last = i === points.length - 1;
            return (
              <g key={p.day}>
                {/* Today is the reading being asked about; the rest is
                    context. The ring keeps the dot legible on the line. */}
                <circle
                  cx={x(i)}
                  cy={y(worst)}
                  r={last ? 4 : 2.25}
                  fill={stroke}
                  stroke="var(--surface)"
                  strokeWidth={last ? 2 : 0}
                />
                {/* The hit target, wider than the dot, so the reading is
                    hoverable without pixel hunting. */}
                <circle cx={x(i)} cy={y(worst)} r={10} fill="transparent">
                  <title>
                    {`${formatDate(p.day)}: ${worst.toFixed(2)} dBm${
                      best !== worst ? ` to ${best.toFixed(2)} dBm` : ''
                    }`}
                  </title>
                </circle>
              </g>
            );
          })}
          {/* The time scale along the bottom. */}
          <line x1={L} x2={W - R} y1={H - B + 4} y2={H - B + 4} stroke="var(--line)" strokeWidth={1} />
          {points.map((p, i) => {
            const tx = x(i);
            const last = i === points.length - 1;
            const t = new Date(p.day).getTime();
            return (
              <g key={p.day}>
                <line x1={tx} x2={tx} y1={H - B + 4} y2={H - B + 8} stroke="var(--line)" strokeWidth={1} />
                {labelled.includes(i) && (
                  <text
                    x={tx}
                    y={H - 6}
                    textAnchor={i === 0 ? 'start' : last ? 'end' : 'middle'}
                    fontSize="10"
                    fill={stroke}
                    fillOpacity={0.85}
                  >
                    {last && Date.now() - t < DAY_MS ? 'today' : dateLabel(t)}
                  </text>
                )}
              </g>
            );
          })}
          </svg>
        </div>
      </div>
      {/* At the bottom of the box (operator's call), reading what the chart
          shows; a degradation verdict with something to say takes its place. */}
      {/* Only a verdict earns a caption line (2026-09-10, shorter card); the
          window is in the title's tooltip. */}
      {verdict?.detail && (
        <p className="mt-auto border-t border-line px-5 py-1.5 text-xs text-muted">{verdict.detail}</p>
      )}
    </Frame>
  );
}

interface TopologyZone {
  id: string;
  name: string;
}
interface TopologySplitter {
  id: string;
  name: string;
  zoneId?: string | null;
  ports?: number | null;
}

/**
 * Where this ONU physically sits.
 *
 * Nothing here is sent to the OLT — a chassis has no idea which splitter its
 * light passes through, and only the person who ran the fibre does. It is
 * recorded so that the next fault on this drop is traceable to the splitter it
 * shares with its neighbours.
 */
function PlacementCard({
  onu,
  onNotice,
}: {
  onu: Onu;
  onNotice: (message: string, tone?: 'info' | 'danger') => void;
}) {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const editable = can('topology.manage');

  const topology = useQuery({
    queryKey: ['topology'],
    queryFn: () =>
      api.get<{ zones: TopologyZone[]; splitters: TopologySplitter[] }>('/topology'),
  });
  const zones = topology.data?.zones ?? [];
  const splitters = topology.data?.splitters ?? [];

  /**
   * Seeded from where the ONU actually IS, not left empty.
   *
   * These three started blank and the stored NAMES were shown as placeholder
   * text, which read as "not placed" for a subscriber who was placed — and did
   * worse than read wrong. Save sends all three fields together, so an operator
   * correcting only the splitter PORT sent `zoneId: null, splitterId: null`
   * with it and silently erased the placement they had come to refine. On an
   * OMCI-only subscriber that record is the only trace of which port fed them.
   *
   * The ids were not on the ONU response at all until now: the detail handler
   * computed them into `placement` and used only the two names.
   *
   * Deliberately initial state rather than an effect: this page re-reads every
   * 15 seconds, and re-seeding on each of those would wipe a half-finished edit
   * under the operator's hands.
   */
  const [zoneId, setZoneId] = useState(onu.zoneId ?? '');
  const [splitterId, setSplitterId] = useState(onu.splitterId ?? '');
  const [splitterPort, setSplitterPort] = useState(
    onu.splitterPort === null || onu.splitterPort === undefined ? '' : String(onu.splitterPort),
  );
  const [dirty, setDirty] = useState(false);
  /**
   * Where the SUBSCRIBER is, beside where the fibre goes: address, a contact
   * line, GPS — the per-ONU fields SmartOLT carries. Their own capability
   * (onu.provision) and their own route, saved by the same button.
   */
  const locationEditable = can('onu.provision');
  const [address, setAddress] = useState(onu.address ?? '');
  const [contact, setContact] = useState(onu.contact ?? '');
  const [latitude, setLatitude] = useState(onu.latitude == null ? '' : String(onu.latitude));
  const [longitude, setLongitude] = useState(onu.longitude == null ? '' : String(onu.longitude));
  const [locationDirty, setLocationDirty] = useState(false);

  const save = useMutation({
    mutationFn: async () => {
      if (editable && dirty) {
        await api.patch(`/onus/${encodeURIComponent(onu.id)}/placement`, {
          zoneId: zoneId || null,
          splitterId: splitterId || null,
          splitterPort: splitterPort.trim() === '' ? null : Number(splitterPort),
        });
      }
      if (locationEditable && locationDirty) {
        const num = (v: string) => (v.trim() === '' ? null : Number(v));
        await api.patch(`/onus/${encodeURIComponent(onu.id)}/location`, {
          address: address.trim() || null,
          contact: contact.trim() || null,
          latitude: num(latitude),
          longitude: num(longitude),
        });
      }
    },
    onSuccess: async () => {
      setDirty(false);
      setLocationDirty(false);
      onNotice('Placement saved.');
      await queryClient.invalidateQueries({ queryKey: ['onu', onu.id] });
    },
    onError: (err) =>
      onNotice(err instanceof Error ? err.message : 'Could not save placement', 'danger'),
  });

  // Splitters in the chosen zone, since a splitter elsewhere is not a plausible
  // answer — but all of them when no zone is picked yet.
  const relevant = zoneId ? splitters.filter((s) => !s.zoneId || s.zoneId === zoneId) : splitters;

  const title = (
    <span className="flex items-center gap-2">
      <MapPin className="size-4 text-muted" />
      Placement
      {onu.splitterName && <Pill tone="primary">{onu.splitterName}</Pill>}
    </span>
  );

  if (zones.length === 0 && splitters.length === 0) {
    return (
      <Card title={title}>
        <EmptyState
          title="No zones or splitters yet"
          description="Build the outside plant on the Topology page, then an ONU can be placed on it. Nothing here is sent to the OLT — a chassis does not know what a splitter is."
        />
      </Card>
    );
  }

  return (
    <Card title={title}>
      <div className="grid gap-3 px-4 py-3 sm:grid-cols-3">
        {/* Typed rather than scrolled: an operator with a few hundred splitters
            knows the name and should not have to hunt down a list for it. */}
        <Field label="Zone">
          <SearchSelect
            value={zoneId}
            disabled={!editable}
            placeholder="Not placed"
            emptyLabel={zoneId ? 'Clear' : 'Not placed'}
            options={zones.map((z) => ({ value: z.id, label: z.name }))}
            onChange={(next) => {
              setZoneId(next);
              // The splitter list narrows to the new zone, so the old choice
              // may no longer be in it.
              setSplitterId('');
              setDirty(true);
            }}
          />
        </Field>
        <Field label="Splitter" hint="The one it hangs off directly, not the feeder above it">
          <SearchSelect
            value={splitterId}
            disabled={!editable}
            placeholder="Not placed"
            emptyLabel={splitterId ? 'Clear' : 'Not placed'}
            options={relevant.map((s) => ({
              value: s.id,
              label: s.name,
              hint: s.ports ? `${s.ports} ports` : null,
            }))}
            onChange={(next) => {
              setSplitterId(next);
              setDirty(true);
            }}
          />
        </Field>
        <Field label="Splitter port" hint="Blank if it has not been traced">
          <Input
            value={splitterPort}
            disabled={!editable}
            onChange={(e) => {
              setSplitterPort(e.target.value);
              setDirty(true);
            }}
            placeholder="—"
          />
        </Field>
      </div>
      <div className="grid gap-3 border-t border-line px-4 py-3 sm:grid-cols-4">
        <Field label="Address">
          <Input
            value={address}
            disabled={!locationEditable}
            onChange={(e) => {
              setAddress(e.target.value);
              setLocationDirty(true);
            }}
            placeholder="—"
            maxLength={300}
          />
        </Field>
        <Field label="Contact">
          <Input
            value={contact}
            disabled={!locationEditable}
            onChange={(e) => {
              setContact(e.target.value);
              setLocationDirty(true);
            }}
            placeholder="phone or name"
            maxLength={200}
          />
        </Field>
        <Field label="Latitude">
          <Input
            value={latitude}
            disabled={!locationEditable}
            onChange={(e) => {
              setLatitude(e.target.value);
              setLocationDirty(true);
            }}
            placeholder="-1.2921"
            className="font-mono"
          />
        </Field>
        <Field label="Longitude">
          <Input
            value={longitude}
            disabled={!locationEditable}
            onChange={(e) => {
              setLongitude(e.target.value);
              setLocationDirty(true);
            }}
            placeholder="36.8219"
            className="font-mono"
          />
        </Field>
      </div>
      {(editable || locationEditable) && (
        <div className="flex items-center justify-between gap-3 border-t border-line px-5 py-3">
          <span className="text-xs text-faint">
            Recorded here only — the OLT is never told, because it has no idea which splitter its
            light passes through.
          </span>
          <Button
            size="sm"
            variant="primary"
            disabled={!(dirty && editable) && !(locationDirty && locationEditable)}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </div>
      )}
    </Card>
  );
}

interface OntServiceConfig {
  found: boolean;
  backupAt: number | null;
  config: {
    port: string;
    ontId: string;
    serialHex: string | null;
    serialEncrypted: boolean;
    description: string | null;
    lineProfileId: number | null;
    serviceProfileId: number | null;
    tr069ProfileId: number | null;
    ipConfig: { mode: string; vlan: number | null; priority: number | null } | null;
    nativeVlans: Array<{ ethPort: number; vlan: number; priority: number | null }>;
    servicePorts: Array<{ id: number; vlan: number; gemport: number | null; userVlan: number | null }>;
  } | null;
  /** The five most recent captures, newest first. */
  history?: Array<{ backupAt: number; config: OntServiceConfig['config'] }>;
}

/**
 * A one-line summary of a provisioning state, used only to tell two captures
 * apart. Comparing the whole object would flag a re-ordered service-port list
 * as a change; these are the fields whose changing actually means something.
 */
function provisionFingerprint(cfg: OntServiceConfig['config']): string {
  if (!cfg) return 'not provisioned';
  return [
    `L${cfg.lineProfileId ?? '-'}`,
    `S${cfg.serviceProfileId ?? '-'}`,
    `T${cfg.tr069ProfileId ?? '-'}`,
    cfg.ipConfig ? `${cfg.ipConfig.mode}/${cfg.ipConfig.vlan ?? '-'}` : 'no-ip',
    [...cfg.servicePorts]
      .sort((a, b) => a.vlan - b.vlan)
      .map((p) => `${p.vlan}:${p.gemport ?? '-'}`)
      .join(','),
  ].join(' ');
}

/**
 * How this ONT is provisioned on the OLT, read out of the latest stored config
 * backup rather than from the chassis — so it costs nothing and is as current
 * as last night's backup, which the header says plainly.
 *
 * Huawei only, because it is parsed from Huawei's config syntax. The ZTE
 * equivalent is the running-config extract the restore path already uses.
 */
function OntServiceConfigCard({ oltId, onuId }: { oltId: string; onuId: string }) {
  const query = useQuery({
    queryKey: ['onu', onuId, 'service-config'],
    queryFn: () =>
      api.get<OntServiceConfig>(
        `/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(onuId)}/service-config`,
      ),
  });

  const title = (
    <span className="flex items-center gap-2">
      <Server className="size-4 text-muted" />
      Provisions
    </span>
  );

  if (query.isLoading) {
    return (
      <Card title={title}>
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      </Card>
    );
  }
  if (query.error) {
    return (
      <Card title={title}>
        <div className="p-5">
          <ErrorNotice error={query.error} />
        </div>
      </Card>
    );
  }

  const data = query.data;
  const cfg = data?.config;

  if (!data?.backupAt) {
    return (
      <Card title={title}>
        <EmptyState
          title="No config backup stored yet"
          description="This reads the OLT's saved running config rather than the chassis. Take a backup on the OLT's Backups tab and it will fill in."
        />
      </Card>
    );
  }

  if (!cfg) {
    return (
      <Card title={title}>
        <EmptyState
          title="Not in the last backup"
          description={`The backup from ${formatDateTime(data.backupAt)} holds no provisioning for this ONT. It may have been authorised since.`}
        />
      </Card>
    );
  }

  const rows: Array<[string, string]> = [
    ['Line profile', cfg.lineProfileId === null ? '—' : String(cfg.lineProfileId)],
    ['Service profile', cfg.serviceProfileId === null ? '—' : String(cfg.serviceProfileId)],
    ['TR-069 profile', cfg.tr069ProfileId === null ? 'not set' : String(cfg.tr069ProfileId)],
    [
      'Management',
      cfg.ipConfig
        ? `${cfg.ipConfig.mode}${cfg.ipConfig.vlan !== null ? ` on VLAN ${cfg.ipConfig.vlan}` : ''}${
            cfg.ipConfig.priority !== null ? ` (priority ${cfg.ipConfig.priority})` : ''
          }`
        : 'none configured',
    ],
    [
      'Serial in the backup',
      cfg.serialEncrypted
        ? 'held encrypted by the chassis'
        : (cfg.serialHex ?? '—'),
    ],
  ];

  return (
    <Card title={title}>
      <p className="border-b border-line px-5 py-2 text-xs text-faint">
        From the config backup taken {formatRelative(data.backupAt)} — not read from the chassis
        now, so a change made since will not appear until the next backup.
      </p>

      <ProvisionHistory history={data.history ?? []} />

      <dl>
        {rows.map(([label, value]) => (
          <div
            key={label}
            className="flex items-baseline justify-between gap-4 border-b border-line px-5 py-2.5"
          >
            <dt className="text-sm text-muted">{label}</dt>
            <dd className="truncate font-mono text-xs text-ink">{value}</dd>
          </div>
        ))}
      </dl>

      {cfg.servicePorts.length > 0 && (
        <Table>
          <THead>
            <th className="th">Service port</th>
            <th className="th">VLAN</th>
            <th className="th">User VLAN</th>
            <th className="th">GEM port</th>
          </THead>
          <TBody>
            {cfg.servicePorts.map((sp) => (
              <TRow key={sp.id}>
                <td className="td font-mono text-xs">{sp.id}</td>
                <td className="td font-mono text-xs">{sp.vlan}</td>
                <td className="td font-mono text-xs text-muted">{sp.userVlan ?? '—'}</td>
                <td className="td font-mono text-xs text-muted">{sp.gemport ?? '—'}</td>
              </TRow>
            ))}
          </TBody>
        </Table>
      )}

      {cfg.nativeVlans.length > 0 && (
        <p className="border-t border-line px-5 py-2.5 text-xs text-muted">
          Native VLANs:{' '}
          {cfg.nativeVlans
            .map((n) => `eth ${n.ethPort} → VLAN ${n.vlan}${n.priority !== null ? ` p${n.priority}` : ''}`)
            .join(' · ')}
        </p>
      )}
    </Card>
  );
}

/**
 * When this ONT's provisioning last changed, across the five most recent
 * captures.
 *
 * One capture says what the ONT is; five say when that last moved, which is
 * the question actually being asked when a subscriber's service stops
 * behaving. Retention keeps seven dailies, so five never reaches past what is
 * stored.
 *
 * Captures that match the one before them are marked as unchanged rather than
 * listed again — a column of five identical rows reads as five events.
 */
function ProvisionHistory({
  history,
}: {
  history: Array<{ backupAt: number; config: OntServiceConfig['config'] }>;
}) {
  if (history.length < 2) return null;

  const rows = history.map((h, i) => {
    const previous = history[i + 1];
    const changed = previous ? provisionFingerprint(h.config) !== provisionFingerprint(previous.config) : false;
    return { ...h, changed, fingerprint: provisionFingerprint(h.config) };
  });
  const lastChange = rows.find((r) => r.changed);

  return (
    <div className="border-b border-line px-5 py-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-xs uppercase tracking-wide text-faint">Last {rows.length} captures</span>
        <span className="text-[11px] text-muted">
          {lastChange
            ? `changed ${formatRelative(lastChange.backupAt)}`
            : 'unchanged across all of them'}
        </span>
      </div>
      <ol className="mt-2 space-y-1">
        {rows.map((r) => (
          <li key={r.backupAt} className="flex flex-wrap items-baseline gap-2 text-[11px]">
            <span className="w-28 shrink-0 text-faint">{formatRelative(r.backupAt)}</span>
            {r.config === null ? (
              <span className="text-faint">not on the chassis yet</span>
            ) : (
              <span className="font-mono text-muted">{r.fingerprint}</span>
            )}
            {r.changed && <Pill tone="warn">changed</Pill>}
          </li>
        ))}
      </ol>
    </div>
  );
}
